/**
 * TODO EKP-3606: formalize ~/pages module and Page components
 **/
import { lazy, Suspense } from 'react';
import { defineMessage } from 'react-intl';
import { Outlet, type RouteObject } from 'react-router';

import type {
  BreadcrumbCrumb,
  RouteHandle,
  RouteResolver,
} from '~/routing/types';
import type { User } from '~/types/User';

import { esignRedirectTabMessages } from '~/common/messages/esignRedirectTabMessages';
import {
  useAdminClientNameCrumb,
  useAutomationClauseCrumb,
  useDashboardNameCrumb,
  useDataExtractionNameCrumb,
  useEntityGroupNameCrumb,
  useEntityTypeNameCrumb,
  useFieldModelNameCrumb,
  useLegacyTicketNameCrumb,
  useLegacyWorkflowNameCrumb,
  usePlaybookNameCrumb,
  useSavedSearchNameCrumb,
  useTicketDocumentNameCrumb,
  useTicketNameCrumb,
  useWorkflowNameCrumb,
} from '~/features/app/breadcrumbResolvers';
import { SidebarNavProvider } from '~/features/app/SidebarNavContext';
import { SidebarNavPreferenceProvider } from '~/features/app/SidebarNavPreferenceContext';
import { AutomationHubLegacyRoute } from '~/features/automation/AutomationHubLegacyRoute';
import { LegacyIntelligenceRouteRedirect } from '~/features/automation/LegacyIntelligenceRouteRedirect';
import { HomeGate } from '~/features/home/components/HomeGate';
import { WorkflowDocumentsRedirect } from '~/features/workflows-clm-evolution/builder/panels/WorkflowDocumentsRedirect';
import { WorkflowSettingsRedirect } from '~/features/workflows-clm-evolution/builder/settings/WorkflowSettingsRedirect';
import { LegacyClmEvolutionRouteRedirect } from '~/features/workflows-clm-evolution/LegacyClmEvolutionRouteRedirect';
import { FlagType } from '~/flags/flags';
import { getFlag } from '~/flags/useFlag';
import { useCurrentUser } from '~/hooks/useCurrentUser';
// Unauthenticated entry points are statically imported, not lazy: they are the
// first paint for every logged-out visitor, and RouteGate's `Suspense`
// fallback is `null`, so a cold chunk fetch would flash the bare white
// document before the auth chrome renders.
import ActivationPage from '~/pages/activation';
import ChangePasswordPage from '~/pages/change-password';
import ExpiredTokenPage from '~/pages/expired-token';
import ForgotPasswordPage from '~/pages/forgot-password';
import LoginPage from '~/pages/login';
import LoginEmbedPage from '~/pages/login/embed';
import LoginEmbedSuccessPage from '~/pages/login/embed/success';
import { canUseAutomation } from '~/permissions/canUseAutomation';
import { canUseRestorationHub } from '~/permissions/canUseDocumentRestore';
import { canUseManagedEntities } from '~/permissions/canUseManagedEntities';
import { canUsePlaybooks } from '~/permissions/canUsePlaybooks';
import { canUseSearchAsHomePage } from '~/permissions/canUseSearchAsHomePage';
import { canUseUpload } from '~/permissions/canUseUpload';
import { canUseUsersPage } from '~/permissions/canUseUsersPage';
import { canUseWorkflow } from '~/permissions/canUseWorkflow';
import { canUseWorkflowAdmin } from '~/permissions/canUseWorkflowAdmin';
import { checkIsAdmin } from '~/permissions/checkIsAdmin';
import { checkIsSuperAdmin } from '~/permissions/checkIsSuperAdmin';
import { checkIsWorkflowOnly } from '~/permissions/checkIsWorkflowOnly';
import { hasHome } from '~/permissions/hasHome';
import { checkIsRbac } from '~/permissions/rbac/checkIsRbac';
import { not } from '~/permissions/utils/not';
import { RoutePathType } from '~/routing/enums';
import { createAdminResolvers } from '~/routing/resolvers/createAdminResolvers';
import { createDefaultResolvers } from '~/routing/resolvers/createDefaultResolvers';
import { createIntegrateWorkflowIntakeFormResolvers } from '~/routing/resolvers/createIntegrateWorkflowIntakeFormResolvers';
import { createPassthroughResolvers } from '~/routing/resolvers/createPassthroughResolvers';
import { createSuperAdminResolvers } from '~/routing/resolvers/createSuperAdminResolvers';
import { createWorkflowAdminResolvers } from '~/routing/resolvers/createWorkflowAdminResolvers';
import { createWorkflowOrClmEvolutionResolvers } from '~/routing/resolvers/createWorkflowOrClmEvolutionResolvers';
import { createWorkflowResolvers } from '~/routing/resolvers/createWorkflowResolvers';
import { useRouting } from '~/routing/useRouting';

import { LoggedInLayout } from './layouts/LoggedInLayout';
import { LoggedOutLayout } from './layouts/LoggedOutLayout';
import { resolveComponent } from './resolveComponent';
import { RouteGate } from './RouteGate';

const ClauseView = lazy(() =>
  import('../ClauseLibrary/ClauseView').then((m) => ({
    default: m.ClauseView,
  })),
);
const BlockCanvas = lazy(() =>
  import('~/features/workflows-clm-evolution/builder/BlockCanvas').then(
    (m) => ({
      default: m.BlockCanvas,
    }),
  ),
);
const TicketDetailsDocumentViewer = lazy(() =>
  import('~/features/workflows-clm-evolution/tickets/tickets-details/documents/document-viewer/TicketDetailsDocumentViewer').then(
    (m) => ({ default: m.TicketDetailsDocumentViewer }),
  ),
);
const NoMatch = lazy(() => import('~/pages/404'));
const ClientsPage = lazy(() => import('~/pages/admin'));
const AdminCreditsPage = lazy(() => import('~/pages/admin/[clientId]/credits'));
const AdminRolesPage = lazy(() => import('~/pages/admin/[clientId]/roles'));
const AdminUsersPage = lazy(() => import('~/pages/admin/[clientId]/users'));
const AdminNavsPage = lazy(() => import('~/pages/admin/console/[clientId]'));
const ApiManagementPage = lazy(
  () => import('~/pages/admin/console/[clientId]/api-management'),
);
const AuditLogsPage = lazy(
  () => import('~/pages/admin/console/[clientId]/audit-logs'),
);
const CopyWorkflowPage = lazy(
  () => import('~/pages/admin/console/[clientId]/copy-workflow'),
);
const CRMPage = lazy(() => import('~/pages/admin/console/[clientId]/crm'));
const DataFieldsPage = lazy(
  () => import('~/pages/admin/console/[clientId]/data-fields'),
);
const FieldManagerPage = lazy(
  () => import('~/pages/admin/console/[clientId]/field-manager'),
);
const HomeSettingsPage = lazy(
  () => import('~/pages/admin/console/[clientId]/home-settings'),
);
const EntityTypesPage = lazy(
  () => import('~/pages/admin/console/[clientId]/entity-types'),
);
const EntityTypeDetailPage = lazy(
  () => import('~/pages/admin/console/[clientId]/entity-types/[entityTypeId]'),
);
const DepartmentsPage = lazy(
  () => import('~/pages/admin/console/[clientId]/departments'),
);
const DepartmentPage = lazy(
  () => import('~/pages/admin/console/[clientId]/departments/[departmentId]'),
);
const DocumentManagementPage = lazy(
  () => import('~/pages/admin/console/[clientId]/document-management'),
);
const EmailIntakePage = lazy(
  () => import('~/pages/admin/console/[clientId]/email-intake'),
);
const IntegrationsPage = lazy(
  () => import('~/pages/admin/console/[clientId]/integrations'),
);
const PartyListsPage = lazy(
  () => import('~/pages/admin/console/[clientId]/manage-party-lists'),
);
const MoreSettingsPage = lazy(
  () => import('~/pages/admin/console/[clientId]/more-settings'),
);
const DocumentProcessingPage = lazy(
  () => import('~/pages/admin/console/[clientId]/process-documents'),
);
const ProcurementPage = lazy(
  () => import('~/pages/admin/console/[clientId]/procurement'),
);
const DocumentsRestore = lazy(
  () => import('~/pages/admin/console/[clientId]/restoration-hub'),
);
const SSOConfigPage = lazy(
  () => import('~/pages/admin/console/[clientId]/saml-configuration'),
);
const SAMLConfigPage = lazy(
  () =>
    import('~/pages/admin/console/[clientId]/saml-configuration/form/[configId]'),
);
const WebhooksPage = lazy(
  () => import('~/pages/admin/console/[clientId]/webhooks'),
);
const AgentForgeInvokePage = lazy(() => import('~/pages/agentforge-invoke'));
const AlertsPage = lazy(() => import('~/pages/alerts/index_deprecated'));

const AutomationHubPage = lazy(() => import('~/pages/automation'));
const AutomationHubDetailPage = lazy(() => import('~/pages/automation/detail'));
const RoutingTablePage = lazy(() => import('~/pages/automation/routing-table'));
const DynamicDataExtractionPage = lazy(
  () => import('~/pages/automation/dynamic-data-extraction'),
);
const ViewDataExtractionPage = lazy(
  () =>
    import('~/pages/automation/dynamic-data-extraction/view-data-extraction/[extractionId]'),
);
const ClauseLibrary = lazy(() => import('~/pages/clause-library'));
const TicketRerouteRedirectPage = lazy(() => import('~/pages/contract'));
const DashboardsPage = lazy(() => import('~/pages/dashboards/[dashboardId]'));
const DocumentRedirect = lazy(() => import('~/pages/document/'));
const DocumentsViewPage = lazy(() => import('~/pages/document/[documentId]'));
const DocumentsPage = lazy(() => import('~/pages/documents/[folderId]'));
const SharedWithMePage = lazy(() => import('~/pages/documents/shared-with-me'));
const DocumentsEntityTypePage = lazy(
  () => import('~/pages/documents/entity-types/[entityTypeId]'),
);
const DocumentsEntityTypeGroupDetailPage = lazy(
  () =>
    import('~/pages/documents/entity-types/[entityTypeId]/entities/[groupId]'),
);
const DownloadEmailAttachmentPage = lazy(
  () => import('~/pages/download-email-attachment'),
);
const DownloadExportReportPage = lazy(
  () => import('~/pages/download-export-report'),
);
const TicketEmailApproveConfirmation = lazy(
  () => import('~/pages/email-action-approve-succeeds'),
);
const TicketEmailRejectConfirmation = lazy(
  () => import('~/pages/email-action-reject-succeeds'),
);
const ExternalDocumentViewerPage = lazy(
  () => import('~/pages/external-document-viewer'),
);
const Fields = lazy(() => import('~/pages/fields'));
const FieldModel = lazy(() => import('~/pages/fields/[modelId]'));
const SelectDebug = lazy(() => import('~/pages/fields/debug'));
const DebugModel = lazy(() => import('~/pages/fields/debug/[modelId]'));
const PermissionFromSSOPage = lazy(
  () => import('~/pages/folders-permission-check'),
);
const GuidelinesRoot = lazy(() => import('~/pages/guidelines'));
const GuidelinesListPage = lazy(() =>
  import('~/features/guidelines/GuidelinesListPage').then((m) => ({
    default: m.GuidelinesListPage,
  })),
);
const GuidelineDetailsPage = lazy(() =>
  import('~/features/guidelines/GuidelineDetailsPage').then((m) => ({
    default: m.GuidelineDetailsPage,
  })),
);
const TicketEmailActionError = lazy(
  () => import('~/pages/invalid-email-action-token'),
);
const ManagedEntityPartiesPage = lazy(
  () => import('~/pages/managed-entity-parties'),
);
const Onboarding = lazy(() => import('~/pages/onboarding'));
const PlaybooksListPage = lazy(() =>
  import('~/features/playbooks/PlaybooksListPage').then((m) => ({
    default: m.PlaybooksListPage,
  })),
);
const PlaybookDetailsPage = lazy(() =>
  import('~/features/playbooks/PlaybookDetailsPage').then((m) => ({
    default: m.PlaybookDetailsPage,
  })),
);
const PlaybookBuilderWizard = lazy(() =>
  import('~/features/playbooks/builder-wizard/PlaybookBuilderWizard').then(
    (m) => ({ default: m.PlaybookBuilderWizard }),
  ),
);
const PlaybookBuilderCreatePage = lazy(() =>
  import('~/features/playbooks/builder-wizard/CreatePlaybookPage').then(
    (m) => ({ default: m.CreatePlaybookPage }),
  ),
);
const PlaybookBuilderGeneratingPage = lazy(() =>
  import('~/features/playbooks/builder-wizard/GeneratingPlaybookPage').then(
    (m) => ({ default: m.GeneratingPlaybookPage }),
  ),
);
const PlaybookBuilderEditDraftPage = lazy(() =>
  import('~/features/playbooks/builder-wizard/EditDraftPage').then((m) => ({
    default: m.EditDraftPage,
  })),
);
const PlaybookBuilderReviewPage = lazy(() =>
  import('~/features/playbooks/builder-wizard/ReviewPlaybookPage').then(
    (m) => ({ default: m.ReviewPlaybookPage }),
  ),
);
const UnifiedFieldsRegistryPlaygroundPage = lazy(
  () => import('~/pages/playground/unified-fields-registry'),
);
const RBACPage = lazy(() => import('~/pages/rbac'));
const SavedDashboards = lazy(() => import('~/pages/saved-dashboards'));
const SavedSearches = lazy(() => import('~/pages/saved-searches'));
const SearchV2 = lazy(() => import('~/pages/search'));
const SettingsPage = lazy(() => import('~/pages/settings'));
const SuperAdminUsersPage = lazy(() => import('~/pages/super-admin/users'));
const TicketEmailApproveComment = lazy(
  () =>
    import('~/pages/tickets/[ticketId]/judgment-results/[judgementResultId]/[jwt]/approve'),
);
const TicketEmailRejectComment = lazy(
  () =>
    import('~/pages/tickets/[ticketId]/judgment-results/[judgementResultId]/[jwt]/reject'),
);
const Unauthorized = lazy(() => import('~/pages/unauthorized'));
const UploadPage = lazy(() => import('~/pages/upload'));
const WordOnlineErrorPage = lazy(
  () => import('~/pages/word-online-invalid-subscription'),
);
const WordOnlinePage = lazy(() => import('~/pages/word-online/[file-id]'));
const WizardTicketForm = lazy(
  () => import('~/pages/workflow/[workflowId]/intake-form/link'),
);
const WssIntakeFormRedirect = lazy(
  () => import('~/pages/integrate-workflow/[workflowId]/intake-form/link'),
);
const WorkflowBuilderDashboardPage = lazy(
  () => import('~/pages/workflow/builder'),
);
const WorkflowBuilderPage = lazy(
  () => import('~/pages/workflow/builder/[workflowId]'),
);
const WorkflowTicketsPage = lazy(() => import('~/pages/workflow/tickets'));
const TicketsViewPage = lazy(
  () => import('~/pages/workflow/tickets/[ticketId]'),
);
const TicketReviewerPage = lazy(
  () => import('~/pages/workflow/tickets/[ticketId]/reviewer/[versionNumber]'),
);
const WorkflowTicketsNewPage = lazy(
  () => import('~/pages/workflow/tickets/new'),
);
const WorkflowCLMEvolutionRequestDetailPage = lazy(
  () => import('~/pages/workflows-clm-evolution/requests/[ticketId]'),
);
const WorkflowCLMEvolutionEsignRedirectPage = lazy(
  () => import('~/pages/workflows-clm-evolution/esign-redirect'),
);
const WorkflowCLMEvolutionTicketsPage = lazy(
  () => import('~/pages/workflows-clm-evolution/tickets'),
);
const WorkflowCLMEvolutionNewRequestPage = lazy(
  () => import('~/pages/workflows-clm-evolution/tickets/new'),
);
const WorkflowCLMEvolutionWorkflowsPage = lazy(
  () => import('~/pages/workflows-clm-evolution/workflows'),
);
const WorkflowCLMEvolutionWorkflowDetailPage = lazy(
  () => import('~/pages/workflows-clm-evolution/workflows/[workflowId]'),
);
const XRayLibrary = lazy(() => import('~/pages/x-ray/library'));
const XRayLibraryAdmin = lazy(() => import('~/pages/x-ray/library/admin'));

const adminConsoleCrumb = defineMessage({
  id: 'Nav.adminConsole',
  defaultMessage: 'Admin Console',
  description: 'Navigation text for Admin Console',
});

/**
 * Every admin console page hangs directly off the console. Unlike the inline
 * `generateNav` trail this replaces, the crumb links through the route's own
 * `:clientId` rather than the signed-in user's — an admin viewing another
 * client saw a console link back to their own.
 */
const adminConsoleTrail: BreadcrumbCrumb[] = [
  { title: adminConsoleCrumb, path: RoutePathType.AdminConsoleClient },
];

/**
 * One trail per top-level sidebar nav group, so a page's crumbs start where the
 * nav does. Each crumb links to the group's own landing destination — the same
 * `pathname` its nav entry uses (see `createSidebarNavlinks` in
 * `~/utils/navLinks`). A group whose landing page is also its first child (Custom
 * AI → Fields, Workflows → Requests) repeats that link in the second crumb,
 * matching what the nav itself shows.
 */
const workflowTrail: BreadcrumbCrumb[] = [
  {
    title: defineMessage({
      id: 'PageHeader.navLinks.workflow',
      defaultMessage: 'Workflows',
      description: 'Workflow navigation link',
    }),
    path: RoutePathType.WorkflowTickets,
  },
];

const workflowsClmEvolutionTrail: BreadcrumbCrumb[] = [
  {
    title: defineMessage({
      id: 'PageHeader.navLinks.workflowsClmEvolution',
      defaultMessage: 'Workflows',
      description: 'Workflows CLM Evolution navigation link',
    }),
    path: RoutePathType.WorkflowCLMEvolutionRequests,
  },
];

const dashboardsTrail: BreadcrumbCrumb[] = [
  {
    title: defineMessage({
      id: 'PageHeader.navLinks.dashboards',
      defaultMessage: 'Dashboards',
      description: 'Dashboards navigation link',
    }),
    path: RoutePathType.Dashboards,
  },
];

const searchTrail: BreadcrumbCrumb[] = [
  {
    title: defineMessage({
      id: 'AppSidebarNav.searchTools',
      defaultMessage: 'Search tools',
      description:
        'Label for the sidebar section that groups search, saved searches, and other search tools.',
    }),
    path: RoutePathType.Search,
  },
];

const savedSearchesTrail: BreadcrumbCrumb[] = [
  ...searchTrail,
  {
    title: defineMessage({
      id: 'Routes.savedSearchesTitle',
      defaultMessage: 'Saved Searches',
      description: 'Title for the saved searches page route',
    }),
    path: RoutePathType.SavedSearches,
  },
];

const libraryTrail: BreadcrumbCrumb[] = [
  {
    title: defineMessage({
      id: 'PageHeader.navLinks.clauseLibrary',
      defaultMessage: 'Library',
      description: 'Clause Library navigation link',
    }),
    path: RoutePathType.Playbooks,
  },
];

const playbooksCrumb = defineMessage({
  id: 'Routes.playbooksTitle',
  defaultMessage: 'Playbooks',
  description: 'Title for the playbooks page route',
});

const playbookBuilderHandle: RouteHandle = {
  title: defineMessage({
    id: 'Routes.playbookBuilderTitle',
    defaultMessage: 'Playbook Builder',
    description: 'Title for the Playbook Builder wizard pages',
  }),
  layout: 'workspace',
  breadcrumbs: [
    ...libraryTrail,
    { title: playbooksCrumb, path: RoutePathType.Playbooks },
  ],
};

const automationHubCrumb = defineMessage({
  id: 'PageHeader.navLinks.automation',
  defaultMessage: 'Automation Hub',
  description: 'Automation Hub navigation link',
});

const customAiCrumb = defineMessage({
  id: 'Routes.customAiCrumb',
  defaultMessage: 'Custom AI',
  description: 'Breadcrumb text for the Custom AI fields page',
});

const clausesCrumb = defineMessage({
  id: 'PageHeader.subNavLinks.automationClauses',
  defaultMessage: 'Clauses',
  description:
    'Clause automation navigation link, shown when the rebrand flag is off',
});

const quickAiCrumb = defineMessage({
  id: 'PageHeader.subNavLinks.automation',
  defaultMessage: 'Quick AI',
  description: 'Clause automation navigation link',
});

const automationHubTrail: BreadcrumbCrumb[] = [
  { title: automationHubCrumb, path: RoutePathType.AutomationHubFields },
];

const customAiRebrandTrail: BreadcrumbCrumb[] = [
  { title: customAiCrumb, path: RoutePathType.CustomAiHubFields },
];

/**
 * Shared by the workflow detail route and the child routes rendered inside it,
 * which are the same page as far as the breadcrumb is concerned. Every route in
 * a nested group has to declare its own trail — the deepest match wins, so a
 * child that declared nothing would drop the ancestors its parent named.
 */
const workflowClmEvolutionDetailTrail: BreadcrumbCrumb[] = [
  ...workflowsClmEvolutionTrail,
  {
    title: defineMessage({
      id: 'Routes.workflowCLMEvolutionWorkflowsTitle',
      defaultMessage: 'Workflow Builder',
      description: 'Title for the workflow builder page route',
    }),
    path: RoutePathType.WorkflowCLMEvolutionWorkflows,
  },
  { resolver: useWorkflowNameCrumb },
];

/**
 * A private route, which renders inside `LoggedInLayout` and therefore under the
 * `AppBreadcrumb` bar. `handle` is required and typed, so every page must state
 * its `title` and its `breadcrumbs` — see `RouteHandle`. Public routes stay on
 * the plain `RouteObject`: they render under `LoggedOutLayout`, which has no
 * breadcrumb bar for a trail to appear in.
 */
type PrivateRouteObject = RouteObject & {
  handle: RouteHandle;
  children?: PrivateRouteObject[];
};

const rootLandingResolvers: RouteResolver[] = [
  {
    permission: checkIsRbac,
    component: RBACPage,
  },
  {
    permission: checkIsWorkflowOnly,
    redirectPath: RoutePathType.WorkflowTickets,
  },
  {
    permission: canUseSearchAsHomePage,
    redirectPath: RoutePathType.Search,
  },
  {
    redirectPath: RoutePathType.Documents,
  },
];

function RootLanding() {
  const user = useCurrentUser();
  const { params } = useRouting();
  const Resolved = resolveComponent(rootLandingResolvers, {
    routeParams: params,
    user,
  });
  return <Resolved />;
}

function RootHomeGate() {
  return <HomeGate fallback={<RootLanding />} />;
}

function HomeSurfaceGate() {
  return <HomeGate fallback={<RootLanding />} />;
}

const privateRouteList: PrivateRouteObject[] = [
  {
    path: RoutePathType.Home,
    element: (
      <RouteGate
        path={RoutePathType.Home}
        resolvers={[
          {
            permission: hasHome,
            component: RootHomeGate,
          },
          ...rootLandingResolvers,
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.homeTitle',
        defaultMessage: 'Home',
        description: 'Title for the home page route',
      }),
      // The landing page names itself in the nav and in its own content; a
      // one-crumb trail reading "Home" above it is noise.
      breadcrumbs: 'none',
      layout: 'page',
    },
  },
  {
    path: RoutePathType.HomeSurface,
    element: (
      <RouteGate
        path={RoutePathType.HomeSurface}
        resolvers={[{ component: HomeSurfaceGate }]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.homeSurfaceTitle',
        defaultMessage: 'Home',
        description: 'Title for the home surface page route',
      }),
      breadcrumbs: 'none',
      layout: 'page',
    },
  },
  {
    path: RoutePathType.Admin,
    element: (
      <RouteGate
        path={RoutePathType.Admin}
        resolvers={createAdminResolvers(ClientsPage)}
      />
    ),
    handle: {
      // Names the page it renders — the clients table — not the console it links
      // to. Reuses the page's own message so the tab title, the crumb and the
      // heading cannot drift.
      title: defineMessage({
        id: 'AdminPage.pageTitle',
        defaultMessage: 'Manage Clients',
        description: 'Page title for admin clients management',
      }),
      // No ancestor: picking a client here is what leads *to* a console, so the
      // inline trail this replaced had the hierarchy backwards.
      breadcrumbs: 'self',
      layout: 'page',
    },
  },
  {
    path: RoutePathType.AdminConsole,
    element: (
      <RouteGate
        path={RoutePathType.AdminConsole}
        resolvers={createAdminResolvers(AdminNavsPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.adminConsoleTitle',
        defaultMessage: 'Admin Console',
        description: 'Title for the admin console page route',
      }),
      breadcrumbs: 'self',
      layout: 'page',
    },
  },
  {
    path: RoutePathType.AdminConsoleClient,
    element: (
      <RouteGate
        path={RoutePathType.AdminConsoleClient}
        resolvers={createAdminResolvers(AdminNavsPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.adminConsoleTitle',
        defaultMessage: 'Admin Console',
        description: 'Title for the admin console page route',
      }),
      breadcrumbs: 'self',
      layout: 'page',
    },
  },
  {
    path: RoutePathType.AdminConsoleClientAuditLogs,
    element: (
      <RouteGate
        path={RoutePathType.AdminConsoleClientAuditLogs}
        permissions={[
          {
            resourceId: 'audit_logs',
            resourceType: 'view',
          },
        ]}
        resolvers={createAdminResolvers(AuditLogsPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.auditLogsTitle',
        defaultMessage: 'Audit Logs',
        description: 'Title for the audit logs page route',
      }),
      breadcrumbs: adminConsoleTrail,
      layout: 'page',
    },
  },
  {
    path: RoutePathType.AdminConsoleClientCopyWorkflow,
    element: (
      <RouteGate
        path={RoutePathType.AdminConsoleClientCopyWorkflow}
        resolvers={createSuperAdminResolvers(CopyWorkflowPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.copyWorkflowTitle',
        defaultMessage: 'Copy Workflow',
        description: 'Title for the copy workflow page route',
      }),
      breadcrumbs: adminConsoleTrail,
      layout: 'page',
    },
  },
  {
    path: RoutePathType.AdminConsoleClientDataFields,
    element: (
      <RouteGate
        path={RoutePathType.AdminConsoleClientDataFields}
        permissions={[
          {
            resourceId: 'data_fields',
            resourceType: 'view',
          },
        ]}
        resolvers={[
          {
            permission: (user: User) =>
              checkIsSuperAdmin(user) ||
              (!getFlag(FlagType.UnifiedFieldsM1) && checkIsAdmin(user)),
            component: DataFieldsPage,
          },
          {
            redirectPath: RoutePathType.Home,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.dataFieldsTitle',
        defaultMessage: 'Data Fields',
        description: 'Title for the data fields page route',
      }),
      breadcrumbs: adminConsoleTrail,
      layout: 'page',
    },
  },
  {
    path: RoutePathType.AdminConsoleClientFieldManager,
    element: (
      <RouteGate
        path={RoutePathType.AdminConsoleClientFieldManager}
        permissions={[
          {
            resourceId: 'field_manager',
            resourceType: 'view',
          },
        ]}
        resolvers={[
          {
            permission: checkIsSuperAdmin,
            component: FieldManagerPage,
          },
          {
            permission: () => !getFlag(FlagType.UnifiedFieldsM1),
            component: NoMatch,
          },
          ...createAdminResolvers(FieldManagerPage),
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.fieldManagerTitle',
        defaultMessage: 'Field Manager',
        description: 'Title for the field manager page route',
      }),
      breadcrumbs: adminConsoleTrail,
      layout: 'page',
    },
  },
  {
    path: RoutePathType.AdminConsoleClientHomeSettings,
    element: (
      <RouteGate
        path={RoutePathType.AdminConsoleClientHomeSettings}
        resolvers={[
          { permission: not(hasHome), component: NoMatch },
          ...createAdminResolvers(HomeSettingsPage),
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.homeSettingsTitle',
        defaultMessage: 'Home Settings',
        description: 'Title for the Home Settings admin page route',
      }),
      breadcrumbs: adminConsoleTrail,
      layout: 'page',
    },
  },
  {
    path: RoutePathType.AdminConsoleClientEntityTypes,
    element: (
      <RouteGate
        path={RoutePathType.AdminConsoleClientEntityTypes}
        flag={FlagType.MesPartiesMvp}
        permissions={[{ resourceId: 'entity_types', resourceType: 'view' }]}
        resolvers={createAdminResolvers(EntityTypesPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.entityTypesTitle',
        defaultMessage: 'Entity Types',
        description: 'Title for the entity types page route',
      }),
      breadcrumbs: adminConsoleTrail,
      layout: 'page',
    },
  },
  {
    path: RoutePathType.AdminConsoleClientEntityTypeDetail,
    element: (
      <RouteGate
        path={RoutePathType.AdminConsoleClientEntityTypeDetail}
        flag={FlagType.MesPartiesMvp}
        permissions={[{ resourceId: 'entity_types', resourceType: 'view' }]}
        resolvers={createAdminResolvers(EntityTypeDetailPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.entityTypeDetailTitle',
        defaultMessage: 'Entity Type',
        description: 'Title for the entity type detail page route',
      }),
      breadcrumbs: [
        ...adminConsoleTrail,
        {
          title: defineMessage({
            id: 'Routes.entityTypesTitle',
            defaultMessage: 'Entity Types',
            description: 'Title for the entity types page route',
          }),
          path: RoutePathType.AdminConsoleClientEntityTypes,
        },
        { resolver: useEntityTypeNameCrumb },
      ],
      layout: 'page',
    },
  },
  {
    path: RoutePathType.AdminConsoleClientDepartments,
    element: (
      <RouteGate
        path={RoutePathType.AdminConsoleClientDepartments}
        permissions={[
          {
            resourceId: 'departments',
            resourceType: 'view',
          },
        ]}
        resolvers={createAdminResolvers(DepartmentsPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.departmentsTitle',
        defaultMessage: 'Departments',
        description: 'Title for the departments page route',
      }),
      breadcrumbs: adminConsoleTrail,
      layout: 'page',
    },
  },
  {
    path: RoutePathType.AdminConsoleClientDepartmentsDepartment,
    element: (
      <RouteGate
        path={RoutePathType.AdminConsoleClientDepartmentsDepartment}
        permissions={[
          {
            resourceId: 'departments',
            resourceType: 'view',
          },
        ]}
        resolvers={createAdminResolvers(DepartmentPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.departmentDetailsTitle',
        defaultMessage: 'Department Details',
        description: 'Title for the department details page route',
      }),
      // TODO: declare this trail once pilot `GET /api/v2/department/detail/:id/`
      // moves to RTK Query. Deferred only because the department name is fetched
      // imperatively into class state, where no resolver can reach it.
      breadcrumbs: 'deferred',
      layout: 'page',
    },
  },
  {
    path: RoutePathType.AdminConsoleClientEmailIntake,
    element: (
      <RouteGate
        path={RoutePathType.AdminConsoleClientEmailIntake}
        permissions={[
          {
            resourceId: 'email_intake',
            resourceType: 'view',
          },
        ]}
        resolvers={[
          {
            permission: checkIsAdmin,
            component: EmailIntakePage,
          },
          {
            redirectPath: RoutePathType.AdminConsoleClient,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.emailIntakeTitle',
        defaultMessage: 'Email Intake',
        description: 'Title for the email intake page route',
      }),
      breadcrumbs: adminConsoleTrail,
      layout: 'page',
    },
  },
  {
    path: RoutePathType.AdminConsoleClientProcurement,
    element: (
      <RouteGate
        path={RoutePathType.AdminConsoleClientProcurement}
        permissions={[
          {
            resourceId: 'integrations',
            resourceType: 'view',
          },
        ]}
        resolvers={createAdminResolvers(ProcurementPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.procurementTitle',
        defaultMessage: 'Procurement',
        description: 'Title for the procurement page route',
      }),
      breadcrumbs: adminConsoleTrail,
      layout: 'page',
    },
  },
  {
    path: RoutePathType.AdminConsoleClientIntegrations,
    element: (
      <RouteGate
        path={RoutePathType.AdminConsoleClientIntegrations}
        permissions={[
          {
            resourceId: 'integrations',
            resourceType: 'view',
          },
        ]}
        resolvers={createAdminResolvers(IntegrationsPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.integrationsTitle',
        defaultMessage: 'Integrations',
        description: 'Title for the integrations page route',
      }),
      breadcrumbs: adminConsoleTrail,
      layout: 'page',
    },
  },
  {
    path: RoutePathType.AdminConsoleClientManagePartyLists,
    element: (
      <RouteGate
        path={RoutePathType.AdminConsoleClientManagePartyLists}
        resolvers={createAdminResolvers(PartyListsPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.managePartyListsTitle',
        defaultMessage: 'Manage Party Lists',
        description: 'Title for the manage party lists page route',
      }),
      breadcrumbs: adminConsoleTrail,
      layout: 'page',
    },
  },
  {
    path: RoutePathType.AdminConsoleClientMoreSettings,
    element: (
      <RouteGate
        path={RoutePathType.AdminConsoleClientMoreSettings}
        permissions={[
          {
            resourceId: 'root_folder_access_settings',
            resourceType: 'view',
          },
          {
            resourceId: 'documents_metadata_bulk_upload',
            resourceType: 'view',
          },
          {
            resourceId: 'clause_samples',
            resourceType: 'view',
          },
          {
            resourceId: 'deleted_documents',
            resourceType: 'view',
          },
        ]}
        resolvers={createAdminResolvers(MoreSettingsPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.moreSettingsTitle',
        defaultMessage: 'More Settings',
        description: 'Title for the more settings page route',
      }),
      breadcrumbs: adminConsoleTrail,
      layout: 'page',
    },
  },
  {
    path: RoutePathType.AdminConsoleClientProcessDocuments,
    element: (
      <RouteGate
        path={RoutePathType.AdminConsoleClientProcessDocuments}
        resolvers={createSuperAdminResolvers(DocumentProcessingPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.processDocumentsTitle',
        defaultMessage: 'Process Documents',
        description: 'Title for the process documents page route',
      }),
      // Explicit leaf: `title` reads "Process Documents"; keep the page's wording.
      breadcrumbs: [
        ...adminConsoleTrail,
        {
          title: defineMessage({
            id: 'ProcessDocuments.navTitle',
            defaultMessage: 'Document Processing',
            description: 'Navigation title for document processing page',
          }),
        },
      ],
      layout: 'page',
    },
  },
  {
    path: RoutePathType.AdminConsoleClientRestorationHub,
    element: (
      <RouteGate
        path={RoutePathType.AdminConsoleClientRestorationHub}
        permissions={[
          {
            resourceId: 'restoration_hub',
            resourceType: 'view',
          },
        ]}
        resolvers={[
          {
            permission: canUseRestorationHub,
            component: DocumentsRestore,
          },
          {
            redirectPath: RoutePathType.Home,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.restorationHubTitle',
        defaultMessage: 'Restoration Hub',
        description: 'Title for the restoration hub page route',
      }),
      breadcrumbs: adminConsoleTrail,
      layout: 'page',
    },
  },
  {
    path: RoutePathType.AdminConsoleClientSamlConfiguration,
    element: (
      <RouteGate
        path={RoutePathType.AdminConsoleClientSamlConfiguration}
        permissions={[
          {
            resourceId: 'sso',
            resourceType: 'view',
          },
        ]}
        resolvers={createAdminResolvers(SSOConfigPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.samlConfigurationTitle',
        defaultMessage: 'SAML Configuration',
        description: 'Title for the SAML configuration page route',
      }),
      // Explicit leaf: `title` reads "SAML Configuration"; keep the page's wording.
      breadcrumbs: [
        ...adminConsoleTrail,
        {
          title: defineMessage({
            id: 'SamlConfiguration.navTitle',
            defaultMessage: 'Single Sign-On (SAML)',
            description: 'Navigation title for SAML configuration page',
          }),
        },
      ],
      layout: 'page',
    },
  },
  {
    path: RoutePathType.AdminConsoleClientSamlConfigurationFormConfig,
    element: (
      <RouteGate
        path={RoutePathType.AdminConsoleClientSamlConfigurationFormConfig}
        permissions={[
          {
            resourceId: 'sso',
            resourceType: 'view',
          },
        ]}
        resolvers={createAdminResolvers(SAMLConfigPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.samlConfigurationFormTitle',
        defaultMessage: 'SAML Configuration',
        description: 'Title for the SAML configuration form page route',
      }),
      breadcrumbs: [
        ...adminConsoleTrail,
        {
          title: defineMessage({
            id: 'Routes.samlConfigurationTitle',
            defaultMessage: 'SAML Configuration',
            description: 'Title for the SAML configuration page route',
          }),
          path: RoutePathType.AdminConsoleClientSamlConfiguration,
        },
      ],
      layout: 'page',
    },
  },
  {
    path: RoutePathType.AdminConsoleClientDocumentManagement,
    element: (
      <RouteGate
        path={RoutePathType.AdminConsoleClientDocumentManagement}
        resolvers={createAdminResolvers(DocumentManagementPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.adminConsoleDocumentManagementTitle',
        defaultMessage: 'Document Management',
        description:
          'Title for the admin console document management page route',
      }),
      breadcrumbs: adminConsoleTrail,
      layout: 'page',
    },
  },
  {
    path: RoutePathType.AdminConsoleClientCRM,
    element: (
      <RouteGate
        path={RoutePathType.AdminConsoleClientCRM}
        permissions={[
          {
            resourceId: 'integrations',
            resourceType: 'view',
          },
        ]}
        resolvers={createAdminResolvers(CRMPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.adminConsoleCRMTitle',
        defaultMessage: 'CRM',
        description: 'Title for the admin console crm page route',
      }),
      breadcrumbs: adminConsoleTrail,
      layout: 'page',
    },
  },
  {
    path: RoutePathType.AdminConsoleClientApiManagement,
    element: (
      <RouteGate
        path={RoutePathType.AdminConsoleClientApiManagement}
        permissions={[
          {
            resourceId: 'integrations',
            resourceType: 'view',
          },
        ]}
        resolvers={createAdminResolvers(ApiManagementPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.adminConsoleApiManagementTitle',
        defaultMessage: 'API Management',
        description: 'Title for the admin console api management page route',
      }),
      breadcrumbs: adminConsoleTrail,
      layout: 'page',
    },
  },
  {
    path: RoutePathType.AdminConsoleClientWebhooks,
    element: (
      <RouteGate
        path={RoutePathType.AdminConsoleClientWebhooks}
        permissions={[
          {
            resourceId: 'integrations',
            resourceType: 'view',
          },
        ]}
        resolvers={createAdminResolvers(WebhooksPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.adminConsoleWebhooksTitle',
        defaultMessage: 'Webhooks',
        description: 'Title for the admin console webhooks page route',
      }),
      breadcrumbs: adminConsoleTrail,
      layout: 'page',
    },
  },
  {
    path: RoutePathType.AdminClientCredits,
    element: (
      <RouteGate
        path={RoutePathType.AdminClientCredits}
        resolvers={createAdminResolvers(AdminCreditsPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.creditsTitle',
        defaultMessage: 'Credits',
        description: 'Title for the credits page route',
      }),
      breadcrumbs: [
        {
          title: defineMessage({
            id: 'Routes.adminTitle',
            defaultMessage: 'Admin',
            description: 'Title for the admin page route',
          }),
          path: RoutePathType.Admin,
        },
        {
          title: defineMessage({
            id: 'Routes.adminConsoleTitle',
            defaultMessage: 'Admin Console',
            description: 'Title for the admin console page route',
          }),
          path: RoutePathType.AdminConsoleClient,
        },
        { resolver: useAdminClientNameCrumb },
      ],
      layout: 'page',
    },
  },
  {
    path: RoutePathType.AdminRoles,
    element: (
      <RouteGate
        path={RoutePathType.AdminRoles}
        permissions={[
          {
            resourceId: 'roles',
            resourceType: 'view',
          },
        ]}
        resolvers={createDefaultResolvers(AdminRolesPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.rolesTitle',
        defaultMessage: 'Roles',
        description: 'Title for the roles page route',
      }),
      breadcrumbs: adminConsoleTrail,
      layout: 'page',
    },
  },
  {
    path: RoutePathType.AdminUsers,
    element: (
      <RouteGate
        path={RoutePathType.AdminUsers}
        permissions={[
          {
            resourceId: 'users',
            resourceType: 'view',
          },
        ]}
        resolvers={[
          {
            permission: canUseUsersPage,
            component: AdminUsersPage,
          },
          {
            redirectPath: RoutePathType.Home,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.usersTitle',
        defaultMessage: 'Users',
        description: 'Title for the users page route',
      }),
      breadcrumbs: adminConsoleTrail,
      layout: 'page',
    },
  },
  {
    path: RoutePathType.Alerts,
    element: (
      <RouteGate
        path={RoutePathType.Alerts}
        permissions={[
          {
            resourceId: 'documents',
            resourceType: 'view',
          },
        ]}
        resolvers={[
          {
            permission: checkIsWorkflowOnly,
            redirectPath: RoutePathType.Home,
          },
          {
            component: AlertsPage,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.alertsTitle',
        defaultMessage: 'Alerts',
        description: 'Title for the alerts page route',
      }),
      breadcrumbs: searchTrail,
      layout: 'page',
    },
  },
  {
    path: RoutePathType.ClauseLibrary,
    element: (
      <RouteGate
        path={RoutePathType.ClauseLibrary}
        permissions={[
          {
            resourceId: 'clause_library',
            resourceType: 'view',
          },
        ]}
        resolvers={[
          {
            /* TODO: Add isClauseLibraryAdmin permission check */
            //permission: canUseClauseLibrary,
            component: ClauseLibrary,
          },
          {
            redirectPath: RoutePathType.Home,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.clauseLibraryTitle',
        defaultMessage: 'Clause Library',
        description: 'Title for the clause library page route',
      }),
      breadcrumbs: libraryTrail,
      layout: 'page',
    },
    children: [
      {
        path: RoutePathType.ClauseLibraryInstance,
        element: (
          <Suspense fallback={null}>
            <ClauseView />
          </Suspense>
        ),
        handle: {
          title: defineMessage({
            id: 'Routes.clauseLibraryTitle',
            defaultMessage: 'Clause Library',
            description: 'Title for the clause library page route',
          }),
          breadcrumbs: libraryTrail,
          layout: 'page',
        },
      },
    ],
  },
  {
    path: RoutePathType.Dashboards,
    element: (
      <RouteGate
        path={RoutePathType.Dashboards}
        permissions={[
          {
            resourceId: 'workflows_dashboard',
            resourceType: 'view',
          },
          {
            resourceId: 'documents_dashboard',
            resourceType: 'view',
          },
        ]}
        resolvers={createDefaultResolvers(DashboardsPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.dashboardsTitle',
        defaultMessage: 'Dashboards',
        description: 'Title for the dashboards page route',
      }),
      breadcrumbs: 'self',
      layout: 'page',
    },
  },
  {
    path: RoutePathType.Dashboard,
    element: (
      <RouteGate
        path={RoutePathType.Dashboard}
        permissions={[
          {
            resourceId: 'workflows_dashboard',
            resourceType: 'view',
          },
          {
            resourceId: 'documents_dashboard',
            resourceType: 'view',
          },
        ]}
        resolvers={createDefaultResolvers(DashboardsPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.dashboardsTitle',
        defaultMessage: 'Dashboards',
        description: 'Title for the dashboards page route',
      }),
      breadcrumbs: [...dashboardsTrail, { resolver: useDashboardNameCrumb }],
      layout: 'page',
    },
  },
  {
    path: RoutePathType.AutomationHub,
    element: (
      <AutomationHubLegacyRoute legacyPath={RoutePathType.AutomationHub}>
        <RouteGate
          path={RoutePathType.AutomationHub}
          permissions={[
            {
              resourceId: 'clause_samples',
              resourceType: 'view',
            },
          ]}
          resolvers={[
            {
              permission: canUseAutomation,
              component: AutomationHubPage,
            },
            {
              permission: canUseWorkflowAdmin,
              redirectPath: RoutePathType.RoutingTable,
            },
            {
              redirectPath: RoutePathType.Home,
            },
          ]}
        />
      </AutomationHubLegacyRoute>
    ),
    handle: {
      title: clausesCrumb,
      breadcrumbs: automationHubTrail,
      layout: 'page',
    },
  },
  {
    path: RoutePathType.CustomAiHub,
    element: (
      <RouteGate
        path={RoutePathType.CustomAiHub}
        permissions={[
          {
            resourceId: 'clause_samples',
            resourceType: 'view',
          },
        ]}
        resolvers={[
          {
            permission: canUseAutomation,
            component: AutomationHubPage,
          },
          {
            permission: canUseWorkflowAdmin,
            redirectPath: RoutePathType.CustomAiRoutingTable,
          },
          {
            redirectPath: RoutePathType.Home,
          },
        ]}
      />
    ),
    handle: {
      title: quickAiCrumb,
      breadcrumbs: customAiRebrandTrail,
      layout: 'page',
    },
  },
  {
    path: RoutePathType.AutomationHubFields,
    element: (
      <AutomationHubLegacyRoute legacyPath={RoutePathType.AutomationHubFields}>
        <RouteGate
          path={RoutePathType.AutomationHubFields}
          resolvers={[
            {
              permission: canUseAutomation,
              component: Fields,
            },
            {
              permission: canUseWorkflowAdmin,
              redirectPath: RoutePathType.RoutingTable,
            },
            {
              redirectPath: RoutePathType.Home,
            },
          ]}
        />
      </AutomationHubLegacyRoute>
    ),
    handle: {
      title: defineMessage({
        id: 'PageHeader.subNavLinks.fields',
        defaultMessage: 'Fields',
        description: 'Fields automation navigation link',
      }),
      breadcrumbs: automationHubTrail,
      layout: 'page',
    },
  },
  {
    path: RoutePathType.CustomAiHubFields,
    element: (
      <RouteGate
        path={RoutePathType.CustomAiHubFields}
        resolvers={[
          {
            permission: canUseAutomation,
            component: Fields,
          },
          {
            permission: canUseWorkflowAdmin,
            redirectPath: RoutePathType.CustomAiRoutingTable,
          },
          {
            redirectPath: RoutePathType.Home,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'PageHeader.subNavLinks.customAiFields',
        defaultMessage: 'Contract Data',
        description: 'Contract Data automation navigation link',
      }),
      breadcrumbs: customAiRebrandTrail,
      layout: 'page',
    },
  },
  {
    path: RoutePathType.AutomationHubFieldModel,
    element: (
      <AutomationHubLegacyRoute
        legacyPath={RoutePathType.AutomationHubFieldModel}
      >
        <RouteGate
          path={RoutePathType.AutomationHubFieldModel}
          resolvers={[
            {
              permission: canUseAutomation,
              component: FieldModel,
            },
            {
              redirectPath: RoutePathType.Home,
            },
          ]}
        />
      </AutomationHubLegacyRoute>
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.automationFieldModelTitle',
        defaultMessage: 'Automation',
        description: 'Title for the automation field model page route',
      }),
      breadcrumbs: [
        ...automationHubTrail,
        { resolver: useFieldModelNameCrumb },
      ],
      layout: 'page',
    },
  },
  {
    path: RoutePathType.CustomAiHubFieldModel,
    element: (
      <RouteGate
        path={RoutePathType.CustomAiHubFieldModel}
        resolvers={[
          {
            permission: canUseAutomation,
            component: FieldModel,
          },
          {
            redirectPath: RoutePathType.Home,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.automationFieldModelTitle',
        defaultMessage: 'Automation',
        description: 'Title for the automation field model page route',
      }),
      breadcrumbs: [
        ...customAiRebrandTrail,
        { resolver: useFieldModelNameCrumb },
      ],
      layout: 'page',
    },
  },
  {
    path: RoutePathType.AutomationHubFieldsDebug,
    element: (
      <AutomationHubLegacyRoute
        legacyPath={RoutePathType.AutomationHubFieldsDebug}
      >
        <RouteGate
          path={RoutePathType.AutomationHubFieldsDebug}
          flag={FlagType.CustomAiModelDebug}
          resolvers={[
            {
              permission: canUseAutomation,
              component: SelectDebug,
            },
            {
              redirectPath: RoutePathType.Home,
            },
          ]}
        />
      </AutomationHubLegacyRoute>
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.automationFieldModelDebugTitle',
        defaultMessage: 'Custom AI Model Debug',
        description: 'Title for the Custom AI model debug page route',
      }),
      breadcrumbs: automationHubTrail,
      layout: 'page',
    },
  },
  {
    path: RoutePathType.CustomAiHubFieldsDebug,
    element: (
      <RouteGate
        path={RoutePathType.CustomAiHubFieldsDebug}
        flag={FlagType.CustomAiModelDebug}
        resolvers={[
          {
            permission: canUseAutomation,
            component: SelectDebug,
          },
          {
            redirectPath: RoutePathType.Home,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.automationFieldModelDebugTitle',
        defaultMessage: 'Custom AI Model Debug',
        description: 'Title for the Custom AI model debug page route',
      }),
      breadcrumbs: customAiRebrandTrail,
      layout: 'page',
    },
  },
  {
    path: RoutePathType.AutomationHubFieldsDebugModel,
    element: (
      <AutomationHubLegacyRoute
        legacyPath={RoutePathType.AutomationHubFieldsDebugModel}
      >
        <RouteGate
          path={RoutePathType.AutomationHubFieldsDebugModel}
          flag={FlagType.CustomAiModelDebug}
          resolvers={[
            {
              permission: canUseAutomation,
              component: DebugModel,
            },
            {
              redirectPath: RoutePathType.Home,
            },
          ]}
        />
      </AutomationHubLegacyRoute>
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.automationFieldModelDebugTitle',
        defaultMessage: 'Custom AI Model Debug',
        description: 'Title for the Custom AI model debug page route',
      }),
      breadcrumbs: [
        ...automationHubTrail,
        {
          title: defineMessage({
            id: 'Routes.automationFieldsDebugCrumb',
            defaultMessage: 'Debug',
            description: 'Breadcrumb text for the Custom AI debug page',
          }),
          path: RoutePathType.AutomationHubFieldsDebug,
        },
        {
          title: defineMessage({
            id: 'Routes.automationFieldModelDebugCrumb',
            defaultMessage: 'Debug Model',
            description: 'Breadcrumb text for the Custom AI model debug page',
          }),
        },
      ],
      layout: 'page',
    },
  },
  {
    path: RoutePathType.CustomAiHubFieldsDebugModel,
    element: (
      <RouteGate
        path={RoutePathType.CustomAiHubFieldsDebugModel}
        flag={FlagType.CustomAiModelDebug}
        resolvers={[
          {
            permission: canUseAutomation,
            component: DebugModel,
          },
          {
            redirectPath: RoutePathType.Home,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.automationFieldModelDebugTitle',
        defaultMessage: 'Custom AI Model Debug',
        description: 'Title for the Custom AI model debug page route',
      }),
      breadcrumbs: [
        ...customAiRebrandTrail,
        {
          title: defineMessage({
            id: 'Routes.automationFieldsDebugCrumb',
            defaultMessage: 'Debug',
            description: 'Breadcrumb text for the Custom AI debug page',
          }),
          path: RoutePathType.CustomAiHubFieldsDebug,
        },
        {
          title: defineMessage({
            id: 'Routes.automationFieldModelDebugCrumb',
            defaultMessage: 'Debug Model',
            description: 'Breadcrumb text for the Custom AI model debug page',
          }),
        },
      ],
      layout: 'page',
    },
  },
  {
    path: RoutePathType.AutomationHubDetail,
    element: (
      <AutomationHubLegacyRoute legacyPath={RoutePathType.AutomationHubDetail}>
        <RouteGate
          path={RoutePathType.AutomationHubDetail}
          permissions={[
            {
              resourceId: 'clause_samples',
              resourceType: 'view',
            },
          ]}
          resolvers={[
            {
              permission: canUseAutomation,
              component: AutomationHubDetailPage,
            },
            {
              redirectPath: RoutePathType.Home,
            },
          ]}
        />
      </AutomationHubLegacyRoute>
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.automationDetailTitle',
        defaultMessage: 'Automation',
        description: 'Title for the automation detail page route',
      }),
      breadcrumbs: [
        ...automationHubTrail,
        { resolver: useAutomationClauseCrumb },
      ],
      layout: 'page',
    },
  },
  {
    path: RoutePathType.CustomAiHubDetail,
    element: (
      <RouteGate
        path={RoutePathType.CustomAiHubDetail}
        permissions={[
          {
            resourceId: 'clause_samples',
            resourceType: 'view',
          },
        ]}
        resolvers={[
          {
            permission: canUseAutomation,
            component: AutomationHubDetailPage,
          },
          {
            redirectPath: RoutePathType.Home,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.automationDetailTitle',
        defaultMessage: 'Automation',
        description: 'Title for the automation detail page route',
      }),
      breadcrumbs: [
        ...customAiRebrandTrail,
        { resolver: useAutomationClauseCrumb },
      ],
      layout: 'page',
    },
  },
  {
    path: RoutePathType.RoutingTable,
    element: (
      <AutomationHubLegacyRoute legacyPath={RoutePathType.RoutingTable}>
        <RouteGate
          path={RoutePathType.RoutingTable}
          permissions={[
            {
              resourceId: 'workflow_templates',
              resourceType: 'view',
            },
          ]}
          resolvers={[
            {
              permission: () => getFlag(FlagType.CLMEvolutionPhase1),
              redirectPath: RoutePathType.Home,
            },
            {
              permission: checkIsWorkflowOnly,
              redirectPath: RoutePathType.WorkflowTickets,
            },
            {
              component: RoutingTablePage,
            },
          ]}
        />
      </AutomationHubLegacyRoute>
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.routingTableTitle',
        defaultMessage: 'Routing Table',
        description: 'Title for the routing table page route',
      }),
      breadcrumbs: automationHubTrail,
      layout: 'page',
    },
  },
  {
    path: RoutePathType.CustomAiRoutingTable,
    element: (
      <RouteGate
        path={RoutePathType.CustomAiRoutingTable}
        permissions={[
          {
            resourceId: 'workflow_templates',
            resourceType: 'view',
          },
        ]}
        resolvers={[
          {
            permission: () => getFlag(FlagType.CLMEvolutionPhase1),
            redirectPath: RoutePathType.Home,
          },
          {
            permission: checkIsWorkflowOnly,
            redirectPath: RoutePathType.WorkflowTickets,
          },
          {
            component: RoutingTablePage,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.routingTableTitle',
        defaultMessage: 'Routing Table',
        description: 'Title for the routing table page route',
      }),
      breadcrumbs: customAiRebrandTrail,
      layout: 'page',
    },
  },
  {
    path: RoutePathType.DynamicDataExtraction,
    element: (
      <AutomationHubLegacyRoute
        legacyPath={RoutePathType.DynamicDataExtraction}
      >
        <RouteGate
          path={RoutePathType.DynamicDataExtraction}
          flag={FlagType.DynamicDataExtraction}
          permissions={[
            {
              resourceId: 'workflow_templates',
              resourceType: 'view',
            },
          ]}
          resolvers={[
            {
              permission: checkIsWorkflowOnly,
              redirectPath: RoutePathType.WorkflowTickets,
            },
            {
              component: DynamicDataExtractionPage,
            },
          ]}
        />
      </AutomationHubLegacyRoute>
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.dataExtractionTitle',
        defaultMessage: 'Data Extraction Table',
        description: 'Title for the data extraction table page route',
      }),
      breadcrumbs: 'self',
      layout: 'page',
    },
  },
  {
    path: RoutePathType.ViewDataExtraction,
    element: (
      <AutomationHubLegacyRoute legacyPath={RoutePathType.ViewDataExtraction}>
        <RouteGate
          path={RoutePathType.ViewDataExtraction}
          flag={FlagType.DynamicDataExtraction}
          permissions={[
            {
              resourceId: 'workflow_templates',
              resourceType: 'view',
            },
          ]}
          resolvers={[
            {
              permission: checkIsWorkflowOnly,
              redirectPath: RoutePathType.WorkflowTickets,
            },
            {
              component: ViewDataExtractionPage,
            },
          ]}
        />
      </AutomationHubLegacyRoute>
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.viewDataExtractionTitle',
        defaultMessage: 'Dynamic Data Extraction',
        description: 'Title for the view data extraction page route',
      }),
      breadcrumbs: [
        {
          title: defineMessage({
            id: 'Routes.dataExtractionTitle',
            defaultMessage: 'Data Extraction Table',
            description: 'Title for the data extraction table page route',
          }),
          path: RoutePathType.DynamicDataExtraction,
        },
        { resolver: useDataExtractionNameCrumb },
      ],
      layout: 'page',
    },
  },
  {
    path: RoutePathType.CustomAiDataExtraction,
    element: (
      <RouteGate
        path={RoutePathType.CustomAiDataExtraction}
        flag={FlagType.DynamicDataExtraction}
        permissions={[
          {
            resourceId: 'workflow_templates',
            resourceType: 'view',
          },
        ]}
        resolvers={[
          {
            permission: checkIsWorkflowOnly,
            redirectPath: RoutePathType.WorkflowTickets,
          },
          {
            component: DynamicDataExtractionPage,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.customAiDataExtractionTitle',
        defaultMessage: 'Data Extraction',
        description: 'Title for the Custom AI data extraction page route',
      }),
      breadcrumbs: customAiRebrandTrail,
      layout: 'page',
    },
  },
  {
    path: RoutePathType.CustomAiDataExtractionDetail,
    element: (
      <RouteGate
        path={RoutePathType.CustomAiDataExtractionDetail}
        flag={FlagType.DynamicDataExtraction}
        permissions={[
          {
            resourceId: 'workflow_templates',
            resourceType: 'view',
          },
        ]}
        resolvers={[
          {
            permission: checkIsWorkflowOnly,
            redirectPath: RoutePathType.WorkflowTickets,
          },
          {
            component: ViewDataExtractionPage,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.viewDataExtractionTitle',
        defaultMessage: 'Dynamic Data Extraction',
        description: 'Title for the view data extraction page route',
      }),
      breadcrumbs: [
        ...customAiRebrandTrail,
        {
          title: defineMessage({
            id: 'Routes.customAiDataExtractionTitle',
            defaultMessage: 'Data Extraction',
            description: 'Title for the Custom AI data extraction page route',
          }),
          path: RoutePathType.CustomAiDataExtraction,
        },
        { resolver: useDataExtractionNameCrumb },
      ],
      layout: 'page',
    },
  },
  {
    path: RoutePathType.AgentForgeInvoke,
    element: (
      <RouteGate
        path={RoutePathType.AgentForgeInvoke}
        flag={FlagType.DevOnlyAgentForgeInvoke}
        resolvers={[
          {
            component: AgentForgeInvokePage,
          },
          {
            redirectPath: RoutePathType.Home,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.agentForgeInvokeTitle',
        defaultMessage: 'Agent Forge Invoke',
        description: 'Title for the agent forge invoke page route',
      }),
      breadcrumbs: 'self',
      layout: 'page',
    },
  },
  {
    path: RoutePathType.PlaygroundUnifiedFieldsRegistry,
    element: (
      <RouteGate
        path={RoutePathType.PlaygroundUnifiedFieldsRegistry}
        flag={FlagType.DevOnlyUnifiedFieldsRegistryPlayground}
        resolvers={[
          {
            component: UnifiedFieldsRegistryPlaygroundPage,
          },
          {
            redirectPath: RoutePathType.Home,
          },
        ]}
      />
    ),
    handle: {
      // Dev-only page — hardcoded on purpose, not run through defineMessage,
      // so it never lands in the i18n extraction bundle.
      title: {
        id: 'Routes.unifiedFieldsRegistryPlaygroundTitle',
        defaultMessage: 'Unified Fields Registry Playground',
      },
      breadcrumbs: 'self',
      layout: 'page',
    },
  },
  {
    path: RoutePathType.Onboarding,
    element: (
      <RouteGate
        path={RoutePathType.Onboarding}
        flag={FlagType.CanduScript}
        resolvers={[
          {
            component: Onboarding,
          },
          {
            redirectPath: RoutePathType.Home,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.onboardingTitle',
        defaultMessage: 'Onboarding',
        description: 'Title for the onboarding page route',
      }),
      breadcrumbs: 'self',
      layout: 'page',
    },
  },
  {
    path: RoutePathType.Documents,
    element: (
      <RouteGate
        path={RoutePathType.Documents}
        permissions={[
          {
            resourceId: 'documents',
            resourceType: 'view',
          },
        ]}
        resolvers={createDefaultResolvers(DocumentsPage)}
      />
    ),
    handle: {
      // Deferred: the folder path is arbitrarily deep and lives in page state,
      // so the page portals its own trail into the breadcrumb bar. See
      // `RouteBreadcrumbs`.
      breadcrumbs: 'deferred',
      title: defineMessage({
        id: 'Routes.documentsTitle',
        defaultMessage: 'Documents',
        description: 'Title for the documents page route',
      }),
      layout: 'page',
    },
  },
  {
    path: RoutePathType.DocumentsEntityType,
    element: (
      <RouteGate
        path={RoutePathType.DocumentsEntityType}
        permissions={[
          { resourceId: 'entity_groups', resourceType: 'view' },
          { resourceId: 'entities', resourceType: 'view' },
          { resourceId: 'entity_types', resourceType: 'view' },
        ]}
        resolvers={createDefaultResolvers(DocumentsEntityTypePage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.documentsEntityTypeTitle',
        defaultMessage: 'Documents',
        description: 'Title for the documents entity type page route',
      }),
      breadcrumbs: [
        {
          title: defineMessage({
            id: 'Routes.documentsTitle',
            defaultMessage: 'Documents',
            description: 'Title for the documents page route',
          }),
          path: RoutePathType.Documents,
        },
        { resolver: useEntityTypeNameCrumb },
      ],
      layout: 'page',
    },
  },
  {
    path: RoutePathType.DocumentsEntityTypeGroupDetail,
    element: (
      <RouteGate
        path={RoutePathType.DocumentsEntityTypeGroupDetail}
        permissions={[
          { resourceId: 'entity_groups', resourceType: 'view' },
          { resourceId: 'entities', resourceType: 'view' },
          { resourceId: 'entity_types', resourceType: 'view' },
        ]}
        resolvers={createDefaultResolvers(DocumentsEntityTypeGroupDetailPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.documentsEntityTypeGroupDetailTitle',
        defaultMessage: 'Entity details',
        description: 'Title for the documents entity type group detail route',
      }),
      breadcrumbs: [
        {
          title: defineMessage({
            id: 'Routes.documentsTitle',
            defaultMessage: 'Documents',
            description: 'Title for the documents page route',
          }),
          path: RoutePathType.Documents,
        },
        {
          resolver: useEntityTypeNameCrumb,
          path: RoutePathType.DocumentsEntityType,
        },
        { resolver: useEntityGroupNameCrumb },
      ],
      layout: 'page',
    },
  },
  {
    path: RoutePathType.DocumentsSharedWithMe,
    element: (
      <RouteGate
        path={RoutePathType.DocumentsSharedWithMe}
        flag={FlagType.SharedWithMe}
        permissions={[
          {
            resourceId: 'documents',
            resourceType: 'view',
          },
        ]}
        resolvers={[
          {
            permission: checkIsWorkflowOnly,
            redirectPath: RoutePathType.Home,
          },
          {
            component: SharedWithMePage,
          },
        ]}
      />
    ),
    handle: {
      breadcrumbs: 'deferred',
      title: defineMessage({
        id: 'Routes.sharedWithMeTitle',
        defaultMessage: 'Shared with me',
        description: 'Title for the shared with me page route',
      }),
      layout: 'page',
    },
  },
  {
    path: RoutePathType.DocumentsSharedWithMeFolder,
    element: (
      <RouteGate
        path={RoutePathType.DocumentsSharedWithMeFolder}
        flag={FlagType.SharedWithMe}
        permissions={[
          {
            resourceId: 'documents',
            resourceType: 'view',
          },
        ]}
        resolvers={[
          {
            permission: checkIsWorkflowOnly,
            redirectPath: RoutePathType.Home,
          },
          {
            component: SharedWithMePage,
          },
        ]}
      />
    ),
    handle: {
      breadcrumbs: 'deferred',
      title: defineMessage({
        id: 'Routes.sharedWithMeTitle',
        defaultMessage: 'Shared with me',
        description: 'Title for the shared with me page route',
      }),
      layout: 'page',
    },
  },
  {
    path: RoutePathType.DocumentsFolder,
    element: (
      <RouteGate
        path={RoutePathType.DocumentsFolder}
        permissions={[
          {
            resourceId: 'documents',
            resourceType: 'view',
          },
        ]}
        resolvers={[
          {
            permission: checkIsWorkflowOnly,
            redirectPath: RoutePathType.Home,
          },
          {
            component: DocumentsPage,
          },
        ]}
      />
    ),
    handle: {
      // Deferred: the folder path is arbitrarily deep and lives in page state,
      // so the page portals its own trail into the breadcrumb bar. See
      // `RouteBreadcrumbs`.
      breadcrumbs: 'deferred',
      title: defineMessage({
        id: 'Routes.documentsFolderTitle',
        defaultMessage: 'Documents',
        description: 'Title for the documents folder page route',
      }),
      layout: 'page',
    },
  },
  {
    path: RoutePathType.Document,
    element: (
      <RouteGate
        path={RoutePathType.Document}
        permissions={[]}
        resolvers={createDefaultResolvers(DocumentsViewPage)}
      />
    ),
    handle: {
      // Deferred: the folder path is arbitrarily deep and lives in page state,
      // so the page portals its own trail into the breadcrumb bar. See
      // `RouteBreadcrumbs`.
      breadcrumbs: 'deferred',
      title: defineMessage({
        id: 'Routes.documentDetailsTitle',
        defaultMessage: 'Document Details',
        description: 'Title for the document details page route',
      }),
      layout: 'workspace',
    },
  },
  {
    path: RoutePathType.DocumentRedirect,
    element: (
      <RouteGate
        path={RoutePathType.DocumentRedirect}
        permissions={[]}
        resolvers={createDefaultResolvers(DocumentRedirect)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.documentRedirectTitle',
        defaultMessage: 'Document Details',
        description: 'Title for the document redirect page route',
      }),
      breadcrumbs: 'none',
      layout: 'chromeless',
    },
  },
  {
    path: RoutePathType.DownloadEmailAttachmentPage,
    element: (
      <RouteGate
        path={RoutePathType.DownloadEmailAttachmentPage}
        resolvers={createDefaultResolvers(DownloadEmailAttachmentPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.downloadEmailAttachmentTitle',
        defaultMessage: 'Download Email Attachment',
        description: 'Title for the download email attachment page route',
      }),
      breadcrumbs: 'self',
      layout: 'page',
    },
  },
  {
    path: RoutePathType.DownloadExportReportPage,
    element: (
      <RouteGate
        path={RoutePathType.DownloadExportReportPage}
        resolvers={createDefaultResolvers(DownloadExportReportPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.downloadExcelExportTitle',
        defaultMessage: 'Download Excel Export',
        description: 'Title for the download Excel export page route',
      }),
      breadcrumbs: 'self',
      layout: 'page',
    },
  },
  {
    path: RoutePathType.FoldersPermissionCheck,
    element: (
      <RouteGate
        path={RoutePathType.FoldersPermissionCheck}
        resolvers={createDefaultResolvers(PermissionFromSSOPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.foldersPermissionCheckTitle',
        defaultMessage: 'Folders Permission Check',
        description: 'Title for the folders permission check page route',
      }),
      breadcrumbs: 'self',
      layout: 'chromeless',
    },
  },
  {
    path: RoutePathType.ManagedEntities,
    element: (
      <RouteGate
        path={RoutePathType.ManagedEntities}
        resolvers={[
          {
            permission: canUseManagedEntities,
            component: ManagedEntityPartiesPage,
          },
          // TODO: add a default redirect
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.managedEntityPartiesTitle',
        defaultMessage: 'managed entity parties',
        description: 'Title for the managed entity parties page route',
      }),
      breadcrumbs: 'self',
      layout: 'page',
    },
  },
  {
    path: RoutePathType.MyDeletedDocuments,
    element: (
      <RouteGate
        path={RoutePathType.MyDeletedDocuments}
        permissions={[
          {
            resourceId: 'documents',
            resourceType: 'view',
          },
        ]}
        resolvers={createPassthroughResolvers(DocumentsRestore)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.myDeletedItemsTitle',
        defaultMessage: 'My Deleted Items',
        description: 'Title for the my deleted items page route',
      }),
      breadcrumbs: 'self',
      layout: 'page',
    },
  },
  {
    path: RoutePathType.Playbooks,
    element: (
      <RouteGate
        path={RoutePathType.Playbooks}
        permissions={[
          {
            resourceId: 'clause_library',
            resourceType: 'view',
          },
        ]}
        resolvers={[
          {
            permission: canUsePlaybooks,
            component: Outlet,
          },
          {
            redirectPath: RoutePathType.ClauseLibrary,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.playbooksTitle',
        defaultMessage: 'Playbooks',
        description: 'Title for the playbooks page route',
      }),
      breadcrumbs: libraryTrail,
      layout: 'page',
    },
    children: [
      {
        path: '',
        element: (
          <Suspense fallback={null}>
            <PlaybooksListPage />
          </Suspense>
        ),
        handle: {
          title: defineMessage({
            id: 'Routes.playbooksTitle',
            defaultMessage: 'Playbooks',
            description: 'Title for the playbooks page route',
          }),
          breadcrumbs: libraryTrail,
          layout: 'page',
        },
      },
      {
        path: RoutePathType.PlaybookDetails,
        element: (
          <Suspense fallback={null}>
            <PlaybookDetailsPage />
          </Suspense>
        ),
        handle: {
          title: defineMessage({
            id: 'Routes.playbooksTitle',
            defaultMessage: 'Playbooks',
            description: 'Title for the playbooks page route',
          }),
          layout: 'bounded',
          breadcrumbs: [
            ...libraryTrail,
            {
              title: defineMessage({
                id: 'Routes.playbooksTitle',
                defaultMessage: 'Playbooks',
                description: 'Title for the playbooks page route',
              }),
              path: RoutePathType.Playbooks,
            },
            { resolver: usePlaybookNameCrumb },
          ],
        } satisfies RouteHandle,
      },
      {
        element: (
          <Suspense fallback={null}>
            <PlaybookBuilderWizard />
          </Suspense>
        ),
        handle: playbookBuilderHandle,
        children: [
          {
            path: RoutePathType.PlaybookBuilderCreate,
            element: (
              <Suspense fallback={null}>
                <PlaybookBuilderCreatePage />
              </Suspense>
            ),
            handle: playbookBuilderHandle,
          },
          {
            path: RoutePathType.PlaybookBuilderGenerating,
            element: (
              <Suspense fallback={null}>
                <PlaybookBuilderGeneratingPage />
              </Suspense>
            ),
            handle: playbookBuilderHandle,
          },
          {
            path: RoutePathType.PlaybookBuilderOrganize,
            element: (
              <Suspense fallback={null}>
                <PlaybookBuilderReviewPage />
              </Suspense>
            ),
            handle: playbookBuilderHandle,
          },
          {
            path: RoutePathType.PlaybookBuilderPreview,
            element: (
              <Suspense fallback={null}>
                <PlaybookBuilderReviewPage isPreview />
              </Suspense>
            ),
            handle: playbookBuilderHandle,
          },
          {
            path: RoutePathType.PlaybookBuilderEditDraft,
            element: (
              <Suspense fallback={null}>
                <PlaybookBuilderEditDraftPage />
              </Suspense>
            ),
            handle: playbookBuilderHandle,
          },
        ],
      },
    ],
  },
  {
    path: RoutePathType.Guidelines,
    element: (
      <RouteGate
        path={RoutePathType.Guidelines}
        permissions={[
          {
            resourceId: 'clause_library',
            resourceType: 'view',
          },
        ]}
        resolvers={[
          {
            permission: canUsePlaybooks,
            component: GuidelinesRoot,
          },
          {
            redirectPath: RoutePathType.ClauseLibrary,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.AiInstructionsTitle',
        defaultMessage: 'Negotiation Requirements',
        description: 'Title for the Negotiation Requirements page route',
      }),
      breadcrumbs: libraryTrail,
      layout: 'page',
    },
    children: [
      {
        path: '',
        element: (
          <Suspense fallback={null}>
            <GuidelinesListPage />
          </Suspense>
        ),
        handle: {
          title: defineMessage({
            id: 'Routes.AiInstructionsTitle',
            defaultMessage: 'Negotiation Requirements',
            description: 'Title for the Negotiation Requirements page route',
          }),
          breadcrumbs: libraryTrail,
          layout: 'page',
        },
      },
      {
        path: RoutePathType.GuidelineDetails,
        element: (
          <Suspense fallback={null}>
            <GuidelineDetailsPage />
          </Suspense>
        ),
        handle: {
          title: defineMessage({
            id: 'Routes.AiInstructionsTitle',
            defaultMessage: 'Negotiation Requirements',
            description: 'Title for the Negotiation Requirements page route',
          }),
          breadcrumbs: libraryTrail,
          layout: 'bounded',
        },
      },
    ],
  },
  {
    path: RoutePathType.SavedSearches,
    element: (
      <RouteGate
        path={RoutePathType.SavedSearches}
        permissions={[
          {
            resourceId: 'documents',
            resourceType: 'view',
          },
        ]}
        resolvers={[
          {
            component: SavedSearches,
          },
          {
            redirectPath: RoutePathType.Home,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.savedSearchesTitle',
        defaultMessage: 'Saved Searches',
        description: 'Title for the saved searches page route',
      }),
      breadcrumbs: searchTrail,
      layout: 'page',
    },
  },
  {
    path: RoutePathType.SavedDashboards,
    element: (
      <RouteGate
        path={RoutePathType.SavedDashboards}
        permissions={[
          {
            resourceId: 'workflows_dashboard',
            resourceType: 'view',
          },
          {
            resourceId: 'documents_dashboard',
            resourceType: 'view',
          },
        ]}
        resolvers={[
          {
            component: SavedDashboards,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.savedDashboardsTitle',
        defaultMessage: 'Saved Dashboards',
        description: 'Title for the saved dashboards page route',
      }),
      breadcrumbs: dashboardsTrail,
      layout: 'page',
    },
  },
  {
    path: RoutePathType.SavedSearch,
    element: (
      <RouteGate
        path={RoutePathType.SavedSearch}
        permissions={[
          {
            resourceId: 'documents',
            resourceType: 'view',
          },
        ]}
        resolvers={[
          {
            component: SearchV2,
          },
          {
            redirectPath: RoutePathType.Search,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.searchTitle',
        defaultMessage: 'Search',
        description: 'Title for the search page route',
      }),
      breadcrumbs: [
        ...savedSearchesTrail,
        { resolver: useSavedSearchNameCrumb },
      ],
      layout: 'page',
    },
  },
  {
    path: RoutePathType.Search,
    element: (
      <RouteGate
        path={RoutePathType.Search}
        permissions={[
          {
            resourceId: 'documents',
            resourceType: 'view',
          },
        ]}
        resolvers={[
          {
            component: SearchV2,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.searchTitle2',
        defaultMessage: 'Search',
        description: 'Title for the search page route',
      }),
      breadcrumbs: searchTrail,
      layout: 'page',
    },
  },
  {
    path: RoutePathType.Settings,
    element: (
      <RouteGate
        path={RoutePathType.Settings}
        resolvers={createPassthroughResolvers(SettingsPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.settingsTitle',
        defaultMessage: 'Settings',
        description: 'Title for the settings page route',
      }),
      breadcrumbs: 'self',
      layout: 'page',
    },
  },
  {
    path: RoutePathType.SuperAdminUsers,
    element: (
      <RouteGate
        path={RoutePathType.SuperAdminUsers}
        resolvers={createSuperAdminResolvers(SuperAdminUsersPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.superAdminUsersTitle',
        defaultMessage: 'Users',
        description: 'Title for the super admin users page route',
      }),
      breadcrumbs: [
        {
          title: defineMessage({
            id: 'AdminPage.pageTitle',
            defaultMessage: 'Manage Clients',
            description: 'Page title for admin clients management',
          }),
          path: RoutePathType.Admin,
        },
      ],
      layout: 'page',
    },
  },
  {
    path: RoutePathType.Upload,
    element: (
      <RouteGate
        path={RoutePathType.Upload}
        permissions={[
          {
            resourceId: 'documents',
            resourceType: 'view',
          },
        ]}
        resolvers={[
          {
            permission: canUseUpload,
            component: UploadPage,
          },
          {
            redirectPath: RoutePathType.Home,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.uploadTitle',
        defaultMessage: 'Upload',
        description: 'Title for the upload page route',
      }),
      breadcrumbs: 'self',
      layout: 'page',
    },
  },
  {
    path: RoutePathType.WorkflowBuilder,
    element: (
      <RouteGate
        path={RoutePathType.WorkflowBuilder}
        permissions={[
          {
            resourceId: 'workflow_templates',
            resourceType: 'view',
          },
        ]}
        resolvers={createWorkflowAdminResolvers(WorkflowBuilderDashboardPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.workflowBuilderTitle',
        defaultMessage: 'Workflow Builder',
        description: 'Title for the workflow builder page route',
      }),
      breadcrumbs: workflowTrail,
      layout: 'page',
    },
  },
  {
    path: RoutePathType.WorkflowBuilderWorkflow,
    element: (
      <RouteGate
        path={RoutePathType.WorkflowBuilderWorkflow}
        permissions={[
          {
            resourceId: 'workflow_templates',
            resourceType: 'view',
          },
        ]}
        resolvers={createWorkflowAdminResolvers(WorkflowBuilderPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.workflowBuilderWorkflowTitle',
        defaultMessage: 'Workflow Builder',
        description: 'Title for the workflow builder workflow page route',
      }),
      breadcrumbs: [
        ...workflowTrail,
        {
          title: defineMessage({
            id: 'Routes.workflowBuilderTitle',
            defaultMessage: 'Workflow Builder',
            description: 'Title for the workflow builder page route',
          }),
          path: RoutePathType.WorkflowBuilder,
        },
        { resolver: useLegacyWorkflowNameCrumb },
      ],
      layout: 'workspace',
    },
  },
  {
    path: RoutePathType.WorkflowIntakeFormLink,
    element: (
      <RouteGate
        path={RoutePathType.WorkflowIntakeFormLink}
        permissions={[
          {
            resourceId: 'workflow_tickets',
            resourceType: 'edit',
          },
          {
            resourceId: 'workflow_templates',
            resourceType: 'view',
          },
        ]}
        resolvers={createWorkflowResolvers(WizardTicketForm)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.workflowIntakeFormTitle',
        defaultMessage: 'Workflow Intake Form',
        description: 'Title for the workflow intake form page route',
      }),
      breadcrumbs: 'self',
      layout: 'workspace',
    },
  },
  {
    path: RoutePathType.IntegrateWorkflowIntakeFormLink,
    element: (
      <RouteGate
        path={RoutePathType.IntegrateWorkflowIntakeFormLink}
        permissions={[
          {
            resourceId: 'workflow_tickets',
            resourceType: 'edit',
          },
          {
            resourceId: 'workflow_templates',
            resourceType: 'view',
          },
        ]}
        resolvers={createIntegrateWorkflowIntakeFormResolvers(
          WssIntakeFormRedirect,
          WizardTicketForm,
        )}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.workflowIntakeFormTitle',
        defaultMessage: 'Workflow Intake Form',
        description: 'Title for the workflow intake form page route',
      }),
      breadcrumbs: 'self',
      layout: 'workspace',
    },
  },
  {
    path: RoutePathType.IntegrateWorkflowAmendIntakeForm,
    element: (
      <RouteGate
        path={RoutePathType.IntegrateWorkflowAmendIntakeForm}
        permissions={[
          {
            resourceId: 'workflow_tickets',
            resourceType: 'edit',
          },
          {
            resourceId: 'workflow_templates',
            resourceType: 'view',
          },
        ]}
        resolvers={createWorkflowResolvers(WizardTicketForm)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.workflowAmendIntakeFormTitle',
        defaultMessage: 'Amend Intake Form',
        description: 'Title for the amend intake form page route',
      }),
      breadcrumbs: 'self',
      layout: 'workspace',
    },
  },
  {
    path: RoutePathType.WorkflowTickets,
    element: (
      <RouteGate
        path={RoutePathType.WorkflowTickets}
        permissions={[
          {
            resourceId: 'workflow_tickets',
            resourceType: 'view',
          },
        ]}
        resolvers={[
          {
            permission: checkIsWorkflowOnly,
            component: WorkflowTicketsPage,
          },
          {
            permission: canUseWorkflow,
            component: WorkflowTicketsPage,
          },
          {
            redirectPath: RoutePathType.Home,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.workflowTicketsTitle',
        defaultMessage: 'Workflow Tickets',
        description: 'Title for the workflow tickets page route',
      }),
      breadcrumbs: workflowTrail,
      layout: 'page',
    },
  },
  {
    path: RoutePathType.WorkflowTicketsNew,
    element: (
      <RouteGate
        path={RoutePathType.WorkflowTicketsNew}
        permissions={[
          {
            resourceId: 'workflow_tickets',
            resourceType: 'view',
          },
        ]}
        resolvers={[
          {
            permission: checkIsWorkflowOnly,
            component: WorkflowTicketsNewPage,
          },
          {
            permission: canUseWorkflow,
            component: WorkflowTicketsNewPage,
          },
          {
            redirectPath: RoutePathType.Home,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.workflowTicketsNewTitle',
        defaultMessage: 'New Ticket',
        description: 'Title for the new ticket page route',
      }),
      breadcrumbs: [
        ...workflowTrail,
        {
          title: defineMessage({
            id: 'Routes.workflowTicketsTitle',
            defaultMessage: 'Workflow Tickets',
            description: 'Title for the workflow tickets page route',
          }),
          path: RoutePathType.WorkflowTickets,
        },
      ],
      layout: 'workspace',
    },
  },
  {
    path: RoutePathType.WorkflowTicketsReroute,
    element: (
      <RouteGate
        path={RoutePathType.WorkflowTicketsReroute}
        resolvers={[
          {
            permission: checkIsWorkflowOnly,
            component: TicketRerouteRedirectPage,
          },
          {
            permission: canUseWorkflow,
            component: TicketRerouteRedirectPage,
          },
          {
            redirectPath: RoutePathType.Home,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.redirectingToTicketTitle',
        defaultMessage: 'Redirecting to Ticket',
        description: 'Title for the redirecting to ticket page route',
      }),
      breadcrumbs: 'none',
      layout: 'chromeless',
    },
  },
  {
    path: RoutePathType.WorkflowTicketsTicket,
    element: (
      <RouteGate
        path={RoutePathType.WorkflowTicketsTicket}
        resolvers={[
          {
            permission: checkIsWorkflowOnly,
            component: TicketsViewPage,
          },
          {
            permission: canUseWorkflow,
            component: TicketsViewPage,
          },
          {
            redirectPath: RoutePathType.Home,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.ticketDetailsTitle',
        defaultMessage: 'Ticket Details',
        description: 'Title for the ticket details page route',
      }),
      breadcrumbs: [
        ...workflowTrail,
        {
          title: defineMessage({
            id: 'Routes.workflowTicketsTitle',
            defaultMessage: 'Workflow Tickets',
            description: 'Title for the workflow tickets page route',
          }),
          path: RoutePathType.WorkflowTickets,
        },
        { resolver: useLegacyTicketNameCrumb },
      ],
      layout: 'page',
    },
  },
  {
    path: RoutePathType.WorkflowCLMEvolutionEsignRedirect,
    element: (
      <RouteGate
        path={RoutePathType.WorkflowCLMEvolutionEsignRedirect}
        flag={FlagType.CLMEvolutionPhase1}
        permissions={[
          {
            resourceId: 'workflow_tickets',
            resourceType: 'view',
          },
        ]}
        resolvers={[{ component: WorkflowCLMEvolutionEsignRedirectPage }]}
      />
    ),
    handle: {
      title: esignRedirectTabMessages.title,
      layout: 'chromeless',
      breadcrumbs: 'none',
    },
  },
  {
    path: RoutePathType.WorkflowCLMEvolutionRequestsDetail,
    element: (
      <RouteGate
        path={RoutePathType.WorkflowCLMEvolutionRequestsDetail}
        flag={FlagType.CLMEvolutionPhase1}
        permissions={[
          {
            resourceId: 'workflow_tickets',
            resourceType: 'view',
          },
        ]}
        resolvers={[{ component: WorkflowCLMEvolutionRequestDetailPage }]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.workflowCLMEvolutionRequestDetailTitle',
        defaultMessage: 'Request Details',
        description:
          'Title for the workflow CLM evolution request details page route',
      }),
      breadcrumbs: [
        ...workflowsClmEvolutionTrail,
        {
          title: defineMessage({
            id: 'Routes.workflowCLMEvolutionTicketsTitle',
            defaultMessage: 'Requests',
            description:
              'Title for the workflow CLM evolution requests page route',
          }),
          path: RoutePathType.WorkflowCLMEvolutionRequests,
        },
        { resolver: useTicketNameCrumb },
      ],
      layout: 'workspace',
    },
    children: [
      {
        path: 'documents/:documentId/:version',
        element: (
          <Suspense fallback={null}>
            <TicketDetailsDocumentViewer />
          </Suspense>
        ),
        handle: {
          title: defineMessage({
            id: 'Routes.workflowCLMEvolutionRequestDetailTitle',
            defaultMessage: 'Request Details',
            description:
              'Title for the workflow CLM evolution request details page route',
          }),
          breadcrumbs: [
            ...workflowsClmEvolutionTrail,
            {
              title: defineMessage({
                id: 'Routes.workflowCLMEvolutionTicketsTitle',
                defaultMessage: 'Requests',
                description:
                  'Title for the workflow CLM evolution requests page route',
              }),
              path: RoutePathType.WorkflowCLMEvolutionRequests,
            },
            {
              resolver: useTicketNameCrumb,
              path: RoutePathType.WorkflowCLMEvolutionRequestsDetail,
            },
            { resolver: useTicketDocumentNameCrumb },
          ],
          layout: 'workspace',
        },
      },
    ],
  },
  {
    path: RoutePathType.WorkflowCLMEvolutionRequests,
    element: (
      <RouteGate
        path={RoutePathType.WorkflowCLMEvolutionRequests}
        flag={FlagType.CLMEvolutionPhase1}
        permissions={[
          {
            resourceId: 'workflow_tickets',
            resourceType: 'view',
          },
        ]}
        resolvers={[{ component: WorkflowCLMEvolutionTicketsPage }]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.workflowCLMEvolutionTicketsTitle',
        defaultMessage: 'Requests',
        description: 'Title for the workflow CLM evolution requests page route',
      }),
      breadcrumbs: workflowsClmEvolutionTrail,
      layout: 'page',
    },
  },
  {
    path: RoutePathType.WorkflowCLMEvolutionNewRequest,
    element: (
      <RouteGate
        path={RoutePathType.WorkflowCLMEvolutionNewRequest}
        flag={FlagType.CLMEvolutionPhase1}
        permissions={[
          {
            resourceId: 'workflow_tickets',
            resourceType: 'view',
          },
        ]}
        resolvers={[{ component: WorkflowCLMEvolutionNewRequestPage }]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.workflowCLMEvolutionNewRequestTitle',
        defaultMessage: 'New Request',
        description:
          'Title for the workflow CLM evolution new request page route',
      }),
      // The trail's leaf depends on which step the `wf` param selects, which a
      // static declaration can't express — the page portals its own.
      breadcrumbs: 'deferred',
      // The intake form scrolls its fields internally under a pinned footer, so
      layout: 'workspace',
    },
  },
  {
    path: RoutePathType.WorkflowCLMEvolutionWorkflows,
    element: (
      <RouteGate
        path={RoutePathType.WorkflowCLMEvolutionWorkflows}
        flag={FlagType.CLMEvolutionPhase1}
        permissions={[
          {
            resourceId: 'workflow_templates',
            resourceType: 'view',
          },
        ]}
        resolvers={[{ component: WorkflowCLMEvolutionWorkflowsPage }]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.workflowCLMEvolutionWorkflowsTitle',
        defaultMessage: 'Workflow Builder',
        description: 'Title for the workflow builder page route',
      }),
      breadcrumbs: workflowsClmEvolutionTrail,
      layout: 'page',
    },
  },
  {
    path: RoutePathType.WorkflowCLMEvolutionWorkflowsDetail,
    element: (
      <RouteGate
        path={RoutePathType.WorkflowCLMEvolutionWorkflowsDetail}
        flag={FlagType.CLMEvolutionPhase1}
        permissions={[
          {
            resourceId: 'workflow_templates',
            resourceType: 'view',
          },
        ]}
        resolvers={[{ component: WorkflowCLMEvolutionWorkflowDetailPage }]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.workflowCLMEvolutionDetailsTitle',
        defaultMessage: 'Workflow Details',
        description: 'Title for the workflow details page route',
      }),
      breadcrumbs: workflowClmEvolutionDetailTrail,
      layout: 'workspace',
    } satisfies RouteHandle,
    children: [
      {
        path: 'workflow',
        element: (
          <Suspense fallback={null}>
            <BlockCanvas />
          </Suspense>
        ),
        handle: {
          title: defineMessage({
            id: 'Routes.workflowCLMEvolutionDetailsTitle',
            defaultMessage: 'Workflow Details',
            description: 'Title for the workflow details page route',
          }),
          breadcrumbs: workflowClmEvolutionDetailTrail,
          layout: 'workspace',
        },
      },
      {
        // Templates moved into a modal over the canvas; keep the retired path
        // working for existing links.
        path: 'documents',
        element: <WorkflowDocumentsRedirect />,
        handle: {
          title: defineMessage({
            id: 'Routes.workflowCLMEvolutionDetailsTitle',
            defaultMessage: 'Workflow Details',
            description: 'Title for the workflow details page route',
          }),
          breadcrumbs: workflowClmEvolutionDetailTrail,
          layout: 'chromeless',
        },
      },
      {
        // Settings moved into the builder side panel; keep the retired path
        // working for existing links.
        path: 'settings',
        element: <WorkflowSettingsRedirect />,
        handle: {
          title: defineMessage({
            id: 'Routes.workflowCLMEvolutionSettingsTitle',
            defaultMessage: 'Workflow Settings',
            description: 'Title for the workflow settings page route',
          }),
          breadcrumbs: workflowClmEvolutionDetailTrail,
          layout: 'chromeless',
        },
      },
    ],
  },
  {
    // The CLM Evolution prefix moved from /workflows-clm-evolution to
    // /workflows — keep the retired prefix working for existing links.
    path: '/workflows-clm-evolution/*',
    element: <LegacyClmEvolutionRouteRedirect />,
    handle: {
      title: defineMessage({
        id: 'Routes.legacyClmEvolutionRedirectTitle',
        defaultMessage: 'Redirecting',
        description: 'Title for the retired CLM Evolution route redirect page',
      }),
      breadcrumbs: 'none',
      layout: 'chromeless',
    },
  },
  {
    // Automation Hub briefly moved to /intelligence — keep that prefix working
    // for links shared during the short window it was live, forwarding to
    // whichever of /automation or /custom-ai is current.
    path: '/intelligence/*',
    element: <LegacyIntelligenceRouteRedirect />,
    handle: {
      title: defineMessage({
        id: 'Routes.legacyIntelligenceRedirectTitle',
        defaultMessage: 'Redirecting',
        description: 'Title for the retired Intelligence route redirect page',
      }),
      breadcrumbs: 'none',
      layout: 'chromeless',
    },
  },
  {
    path: RoutePathType.WorkflowTicketsTicketReviewerVersionNumber,
    element: (
      <RouteGate
        path={RoutePathType.WorkflowTicketsTicketReviewerVersionNumber}
        permissions={[
          {
            resourceId: 'workflow_tickets',
            resourceType: 'view',
          },
        ]}
        resolvers={createWorkflowResolvers(TicketReviewerPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.ticketVersionTitle',
        defaultMessage: 'Ticket Version',
        description: 'Title for the ticket version page route',
      }),
      breadcrumbs: [
        ...workflowTrail,
        {
          title: defineMessage({
            id: 'Routes.workflowTicketsTitle',
            defaultMessage: 'Workflow Tickets',
            description: 'Title for the workflow tickets page route',
          }),
          path: RoutePathType.WorkflowTickets,
        },
        {
          resolver: useLegacyTicketNameCrumb,
          path: RoutePathType.WorkflowTicketsTicket,
        },
      ],
      layout: 'workspace',
    },
  },
  {
    path: RoutePathType.WordOnlineFile,
    element: (
      <RouteGate
        path={RoutePathType.WordOnlineFile}
        permissions={[
          {
            resourceId: 'workflow_tickets',
            resourceType: 'view',
          },
        ]}
        resolvers={createWorkflowOrClmEvolutionResolvers(WordOnlinePage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.wordForTheWebTitle',
        defaultMessage: 'Word for the web',
        description: 'Title for the Word for the web page route',
      }),
      breadcrumbs: 'self',
      layout: 'chromeless',
    },
  },
  {
    path: RoutePathType.XRayLibrary,
    element: (
      <AutomationHubLegacyRoute legacyPath={RoutePathType.XRayLibrary}>
        <RouteGate
          path={RoutePathType.XRayLibrary}
          flag={FlagType.DocumentXRayPrebuiltModels}
          resolvers={[
            {
              permission: canUseAutomation,
              component: XRayLibrary,
            },
            {
              redirectPath: RoutePathType.Home,
            },
          ]}
        />
      </AutomationHubLegacyRoute>
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.xRayModelLibraryTitle',
        defaultMessage: 'Custom AI Model Library',
        description: 'Title for the Custom AI model library page route',
      }),
      breadcrumbs: automationHubTrail,
      layout: 'page',
    },
  },
  {
    path: RoutePathType.XRayLibraryAdmin,
    element: (
      <AutomationHubLegacyRoute legacyPath={RoutePathType.XRayLibraryAdmin}>
        <RouteGate
          path={RoutePathType.XRayLibraryAdmin}
          flag={FlagType.DocumentXRayPrebuiltModelsAdmin}
          resolvers={[
            {
              permission: canUseAutomation,
              component: XRayLibraryAdmin,
            },
            {
              redirectPath: RoutePathType.Home,
            },
          ]}
        />
      </AutomationHubLegacyRoute>
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.xRayModelLibraryManagementTitle',
        defaultMessage: 'Custom AI Model Library Management',
        description:
          'Title for the Custom AI model library management page route',
      }),
      breadcrumbs: [
        ...automationHubTrail,
        {
          title: defineMessage({
            id: 'Routes.xRayModelLibraryCrumb',
            defaultMessage: 'Model Library',
            description: 'Breadcrumb text for the Custom AI model library page',
          }),
          path: RoutePathType.XRayLibrary,
        },
        {
          title: defineMessage({
            id: 'Routes.xRayModelLibraryAdminCrumb',
            defaultMessage: 'Admin Management',
            description:
              'Breadcrumb text for the Custom AI model library admin management page',
          }),
        },
      ],
      layout: 'page',
    },
  },
  {
    path: RoutePathType.CustomAiLibrary,
    element: (
      <RouteGate
        path={RoutePathType.CustomAiLibrary}
        flag={FlagType.DocumentXRayPrebuiltModels}
        resolvers={[
          {
            permission: canUseAutomation,
            component: XRayLibrary,
          },
          {
            redirectPath: RoutePathType.Home,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.xRayModelLibraryTitle',
        defaultMessage: 'Custom AI Model Library',
        description: 'Title for the Custom AI model library page route',
      }),
      breadcrumbs: customAiRebrandTrail,
      layout: 'page',
    },
  },
  {
    path: RoutePathType.CustomAiLibraryAdmin,
    element: (
      <RouteGate
        path={RoutePathType.CustomAiLibraryAdmin}
        flag={FlagType.DocumentXRayPrebuiltModelsAdmin}
        resolvers={[
          {
            permission: canUseAutomation,
            component: XRayLibraryAdmin,
          },
          {
            redirectPath: RoutePathType.Home,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.xRayModelLibraryManagementTitle',
        defaultMessage: 'Custom AI Model Library Management',
        description:
          'Title for the Custom AI model library management page route',
      }),
      breadcrumbs: [
        ...customAiRebrandTrail,
        {
          title: defineMessage({
            id: 'Routes.xRayModelLibraryCrumb',
            defaultMessage: 'Model Library',
            description: 'Breadcrumb text for the Custom AI model library page',
          }),
          path: RoutePathType.CustomAiLibrary,
        },
        {
          title: defineMessage({
            id: 'Routes.xRayModelLibraryAdminCrumb',
            defaultMessage: 'Admin Management',
            description:
              'Breadcrumb text for the Custom AI model library admin management page',
          }),
        },
      ],
      layout: 'page',
    },
  },
];

const publicRouteList: RouteObject[] = [
  {
    path: RoutePathType.Activation,
    element: (
      <RouteGate
        path={RoutePathType.Activation}
        resolvers={createPassthroughResolvers(ActivationPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.activationTitle',
        defaultMessage: 'Activate {brandName}',
        description: 'Title for the activation page route',
      }),
    },
  },
  {
    path: RoutePathType.ChangePassword,
    element: (
      <RouteGate
        path={RoutePathType.ChangePassword}
        resolvers={createPassthroughResolvers(ChangePasswordPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.changePasswordTitle',
        defaultMessage: 'Change password',
        description: 'Title for the change password page route',
      }),
    },
  },
  {
    path: RoutePathType.ExternalDocumentViewer,
    element: (
      <RouteGate
        path={RoutePathType.ExternalDocumentViewer}
        resolvers={createPassthroughResolvers(ExternalDocumentViewerPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.evisortDocumentViewerTitle',
        defaultMessage: '{brandName} Document viewer',
        description: 'Title for the document viewer page route',
      }),
    },
  },
  {
    path: RoutePathType.ExpiredToken,
    element: (
      <RouteGate
        path={RoutePathType.ExpiredToken}
        resolvers={createPassthroughResolvers(ExpiredTokenPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.expiredTokenTitle',
        defaultMessage: 'Expired token',
        description: 'Title for the expired token page route',
      }),
    },
  },
  {
    path: RoutePathType.ForgotPassword,
    element: (
      <RouteGate
        path={RoutePathType.ForgotPassword}
        resolvers={createPassthroughResolvers(ForgotPasswordPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.resetPasswordTitle',
        defaultMessage: 'Reset password',
        description: 'Title for the reset password page route',
      }),
    },
  },
  {
    path: RoutePathType.Login,
    element: (
      <RouteGate
        path={RoutePathType.Login}
        resolvers={createPassthroughResolvers(LoginPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.logInTitle',
        defaultMessage: 'Log In',
        description: 'Title for the log in page route',
      }),
    },
  },
  {
    path: RoutePathType.LoginEmbed,
    element: (
      <RouteGate
        path={RoutePathType.LoginEmbed}
        resolvers={createPassthroughResolvers(LoginEmbedPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.logInEmbedTitle',
        defaultMessage: 'Log In',
        description: 'Title for the log in embed page route',
      }),
    },
  },
  {
    path: RoutePathType.LoginEmbedSuccess,
    element: (
      <RouteGate
        path={RoutePathType.LoginEmbedSuccess}
        resolvers={createPassthroughResolvers(LoginEmbedSuccessPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.logInSuccessfulTitle',
        defaultMessage: 'Log in successful',
        description: 'Title for the log in successful page route',
      }),
    },
  },
  {
    path: RoutePathType.Unauthorized,
    element: (
      <RouteGate
        path={RoutePathType.Unauthorized}
        resolvers={createPassthroughResolvers(Unauthorized)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.unauthorizedTitle',
        defaultMessage: 'Unauthorized',
        description: 'Title for the unauthorized page route',
      }),
    },
  },
  {
    path: RoutePathType.TicketEmailApproveConfirmation,
    element: (
      <RouteGate
        path={RoutePathType.TicketEmailApproveConfirmation}
        resolvers={createPassthroughResolvers(TicketEmailApproveConfirmation)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.ticketApproveViaEmailTitle',
        defaultMessage: 'Ticket Approve Via Email',
        description: 'Title for the ticket approve via email page route',
      }),
    },
  },
  {
    path: RoutePathType.TicketEmailRejectConfirmation,
    element: (
      <RouteGate
        path={RoutePathType.TicketEmailRejectConfirmation}
        resolvers={createPassthroughResolvers(TicketEmailRejectConfirmation)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.ticketRejectViaEmailTitle',
        defaultMessage: 'Ticket Reject Via Email',
        description: 'Title for the ticket reject via email page route',
      }),
    },
  },
  {
    path: RoutePathType.TicketEmailActionError,
    element: (
      <RouteGate
        path={RoutePathType.TicketEmailActionError}
        resolvers={createPassthroughResolvers(TicketEmailActionError)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.ticketActionLinkInvalidTitle',
        defaultMessage: 'Ticket Action Link Invalid',
        description: 'Title for the ticket action link invalid page route',
      }),
    },
  },
  {
    path: RoutePathType.TicketEmailApproveComment,
    element: (
      <RouteGate
        path={RoutePathType.TicketEmailApproveComment}
        resolvers={createPassthroughResolvers(TicketEmailApproveComment)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.ticketApproveWithCommentViaEmailTitle',
        defaultMessage: 'Ticket Approve With Comment Via Email',
        description:
          'Title for the ticket approve with comment via email page route',
      }),
    },
  },
  {
    path: RoutePathType.TicketEmailRejectComment,
    element: (
      <RouteGate
        path={RoutePathType.TicketEmailRejectComment}
        resolvers={createPassthroughResolvers(TicketEmailRejectComment)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.ticketRejectWithCommentViaEmailTitle',
        defaultMessage: 'Ticket Reject With Comment Via Email',
        description:
          'Title for the ticket reject with comment via email page route',
      }),
    },
  },
  {
    path: RoutePathType.WordOnlineError,
    element: (
      <RouteGate
        path={RoutePathType.WordOnlineError}
        resolvers={createPassthroughResolvers(WordOnlineErrorPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.wordForTheWebErrorTitle',
        defaultMessage: 'Word for the web Error',
        description: 'Title for the Word for the web error page route',
      }),
    },
  },
];

const notFoundRoute: PrivateRouteObject = {
  path: RoutePathType.NotFound,
  element: (
    <RouteGate
      path={RoutePathType.NotFound}
      resolvers={createPassthroughResolvers(NoMatch)}
    />
  ),
  handle: {
    title: defineMessage({
      id: 'Routes.notFoundTitle',
      defaultMessage: 'Not found',
      description: 'Title for the not found page route',
    }),
    breadcrumbs: 'self',
    layout: 'page',
  },
};

export const publicRoutes = publicRouteList;
export const privateRoutes = [...privateRouteList, notFoundRoute];

/** These route paths are reserved, and cannot be assigned as client workspaces or URL subroutes */
export const reservedRoutePaths: RoutePathType[] = [
  ...publicRouteList
    .map((route) => route.path as RoutePathType)
    .filter((path): path is RoutePathType => typeof path === 'string'),
  RoutePathType.FoldersPermissionCheck,
];

export const routes: RouteObject[] = [
  { element: <LoggedOutLayout />, children: publicRouteList },
  {
    // Sidebar nav preferences only mean anything once signed in. Scoping the
    // providers here instead of the always-on AppContents shell stops their
    // rolodex query from firing on clientless routes like /login or
    // /activation, where there's no session and it would just 401.
    element: (
      <SidebarNavPreferenceProvider>
        <SidebarNavProvider>
          <LoggedInLayout />
        </SidebarNavProvider>
      </SidebarNavPreferenceProvider>
    ),
    children: privateRoutes,
  },
];
